import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, BarChart3, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, Download, Info, MessageSquare, Wand2, X } from "lucide-react";
import { DEFAULT_ATTENDANCE_PERIODS } from "@triedge/attendance-domain";
import {
  ResponsiveSurfaceTabs,
  type SurfaceTab,
} from "@triedge/domain-surface-primitives";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import type {
  AttendanceAuditEvent,
  AttendanceCaptureContext,
  AttendanceCellRecord,
  AttendanceControlPlaneConfig,
  AttendanceContextExportResult,
  AttendanceCsvExportResult,
  AttendanceFeatureBundle,
  AttendanceImportStatus,
  AttendanceOverview,
  AttendanceOverviewRollingWindow,
  AttendanceRecordsFilter,
  AttendanceRecordsGrid,
  AttendanceStatus,
  AttendanceStudentHistory,
} from "../types";

const EMPTY_CONFIG: AttendanceControlPlaneConfig = {
  divisions: [],
  grades: [],
  classrooms: [],
  periods: [],
  rosterMemberships: [],
  schoolCalendar: { activeWeekdays: [], defaultStatus: "present" },
  updatedAt: 0,
  updatedBy: "",
};

const SCHOOL_TIME_ZONE = "America/New_York";

const schoolTodayIso = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: SCHOOL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const partByType = new Map(parts.map((part) => [part.type, part.value]));
  return `${partByType.get("year")}-${partByType.get("month")}-${partByType.get("day")}`;
};

const DEFAULT_CONTEXT: AttendanceCaptureContext = {
  config: EMPTY_CONFIG,
  currentSchoolDate: schoolTodayIso(),
  currentWeekStart: "",
  currentWeekEnd: "",
  firstUnrecordedClassroomId: null,
  unrecordedClassroomIds: [],
  teacherPreview: { previewOnly: true, writesAllowed: false },
};

const statusOrder: AttendanceStatus[] = ["present", "absent", "late", "excused"];
const STATUS_PICKER_CLOSE_DELAY_MS = 700;
const STATUS_PICKER_SWITCH_DELAY_MS = 180;
const STATUS_PICKER_WIDTH = 140;
const STATUS_PICKER_ESTIMATED_HEIGHT = 146;
const STATUS_PICKER_GAP = 4;
const YSTNJ_NIGHT_MODE_ATTRIBUTE = "data-ystnj-night-mode";
const STATUS_UI_LABELS: Record<AttendanceStatus, { label: string; shortLabel: string }> = {
  present: { label: "Present", shortLabel: "P" },
  absent: { label: "Absent", shortLabel: "A" },
  late: { label: "Late", shortLabel: "L" },
  excused: { label: "Excused", shortLabel: "E" },
};
const OVERVIEW_WINDOW_OPTIONS: AttendanceOverviewRollingWindow[] = [7, 30, 60, 90];
const STATUS_VISUALS = {
  present: {
    background: "rgba(40, 226, 167, 0.10)",
    border: "rgba(40, 226, 167, 0.20)",
    color: "#404F4D",
    active: "#28E2A7",
    dot: "#28E2A7",
  },
  absent: {
    background: "rgba(254, 44, 59, 0.10)",
    border: "rgba(254, 44, 59, 0.20)",
    color: "#404F4D",
    active: "#FE2C3B",
    dot: "#FE2C3B",
  },
  late: {
    background: "rgba(255, 227, 184, 0.65)",
    border: "rgba(245, 158, 11, 0.20)",
    color: "#404F4D",
    active: "#FFE3B8",
    dot: "#D97706",
  },
  excused: {
    background: "rgba(38, 151, 162, 0.12)",
    border: "rgba(38, 151, 162, 0.20)",
    color: "#404F4D",
    active: "#2697A2",
    dot: "#2697A2",
  },
} satisfies Record<AttendanceStatus, {
  background: string;
  border: string;
  color: string;
  active: string;
  dot: string;
}>;
const statusShortcutByKey = new Map<string, AttendanceStatus>(
  statusOrder.map((status) => [STATUS_UI_LABELS[status].shortLabel.toLowerCase(), status]),
);
const nextAttendanceStatus = (status: AttendanceStatus): AttendanceStatus => {
  const currentIndex = statusOrder.indexOf(status);
  return statusOrder[(currentIndex + 1) % statusOrder.length] ?? statusOrder[0];
};
const useIsNarrowViewport = (maxWidth: number) => {
  const [isNarrow, setIsNarrow] = useState(() => (
    typeof window === "undefined" ? false : window.innerWidth <= maxWidth
  ));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => setIsNarrow(window.innerWidth <= maxWidth);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [maxWidth]);

  return isNarrow;
};
const teacherStatusButtonClassName = (status: AttendanceStatus, active: boolean) => {
  const base =
    "min-w-[64px] flex-1 rounded-[6px] border px-2 py-1.5 text-[13px] font-medium leading-[19.5px] transition-colors sm:flex-none sm:px-3";
  if (active && status === "present") return `${base} border-[#28E2A7] bg-[#28E2A7] text-white`;
  if (active && status === "absent") return `${base} border-[#FE2C3B] bg-[#FE2C3B] text-white`;
  if (active && status === "late") return `${base} border-[#FFE3B8] bg-[#FFE3B8] text-[#A06000]`;
  if (active && status === "excused") return `${base} border-[#2697A2] bg-[#2697A2] text-white`;
  if (status === "present") return `${base} border-transparent bg-transparent text-[#237062] hover:bg-[rgba(40,226,167,0.10)]`;
  if (status === "absent") return `${base} border-transparent bg-transparent text-[#CC1020] hover:bg-[rgba(254,44,59,0.10)]`;
  if (status === "late") return `${base} border-transparent bg-transparent text-[#A06000] hover:bg-[rgba(255,227,184,0.50)]`;
  return `${base} border-transparent bg-transparent text-[#1A5A83] hover:bg-[rgba(38,151,162,0.10)]`;
};

const teacherStatusButtonStyle = (
  status: AttendanceStatus,
  active: boolean,
): CSSProperties => {
  const statusColors = {
    present: { activeBackground: "#28E2A7", inactiveText: "#237062" },
    absent: { activeBackground: "#FE2C3B", inactiveText: "#CC1020" },
    late: { activeBackground: "#FFE3B8", activeText: "#A06000", inactiveText: "#A06000" },
    excused: { activeBackground: "#2697A2", inactiveText: "#1A5A83" },
  } satisfies Record<AttendanceStatus, { activeBackground: string; activeText?: string; inactiveText: string }>;
  const colors = statusColors[status];
  if (active) {
    return {
      backgroundColor: colors.activeBackground,
      borderColor: colors.activeBackground,
      color: "activeText" in colors ? colors.activeText : "#FFFFFF",
    };
  }
  return {
    backgroundColor: "transparent",
    borderColor: "transparent",
    color: colors.inactiveText,
  };
};

const statusCellClassName = (status: AttendanceStatus) => {
  void status;
  return "hover:brightness-[0.99]";
};

const statusCellStyle = (status: AttendanceStatus): CSSProperties => {
  const visual = STATUS_VISUALS[status];
  return {
    backgroundColor: visual.background,
    borderColor: visual.border,
    color: visual.color,
  };
};

const isYstnjNightModeActive = () => (
  typeof document !== "undefined"
  && document.documentElement.getAttribute(YSTNJ_NIGHT_MODE_ATTRIBUTE) === "true"
);

const useYstnjNightModeAttribute = () => {
  const [enabled, setEnabled] = useState(isYstnjNightModeActive);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const sync = () => setEnabled(isYstnjNightModeActive());
    sync();
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [YSTNJ_NIGHT_MODE_ATTRIBUTE],
    });
    return () => observer.disconnect();
  }, []);

  return enabled;
};

const useMediaQuery = (query: string, defaultValue = false) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return defaultValue;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia(query);
    const sync = () => setMatches(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener("change", sync);
    return () => mediaQuery.removeEventListener("change", sync);
  }, [query]);

  return matches;
};

const statusPickerBadgeStyle = (status: AttendanceStatus, active: boolean, nightMode: boolean): CSSProperties => {
  const inactiveColor =
    status === "present"
      ? (nightMode ? "#8FE7CC" : "var(--jungle)")
      : status === "absent"
        ? (nightMode ? "#FF8790" : "var(--strawberry)")
        : status === "late"
          ? (nightMode ? "#FFD391" : "#9a5a00")
          : (nightMode ? "#93C7E6" : "#0f5f9a");
  return {
    width: 24,
    minWidth: 24,
    height: 24,
    background: active
      ? "rgba(255,255,255,0.22)"
      : nightMode
        ? "rgba(255,255,255,0.08)"
        : "var(--muted)",
    color: active ? "currentColor" : inactiveColor,
  };
};

const statusPickerOptionStyle = (
  status: AttendanceStatus,
  active: boolean,
  hovered: boolean,
  nightMode: boolean,
): CSSProperties => {
  const activeColors = {
    present: { background: "#28E2A7", color: "#FFFFFF" },
    absent: { background: "#FE2C3B", color: "#FFFFFF" },
    late: { background: "#FFE3B8", color: "#A06000" },
    excused: { background: "#2697A2", color: "#FFFFFF" },
  } satisfies Record<AttendanceStatus, { background: string; color: string }>;
  const inactiveColors = nightMode
    ? {
      present: "#8FE7CC",
      absent: "#FF8790",
      late: "#FFD391",
      excused: "#93C7E6",
    }
    : {
      present: "#237062",
      absent: "#CC1020",
      late: "#A06000",
      excused: "#1A5A83",
    } satisfies Record<AttendanceStatus, string>;
  const hoverBackgrounds = nightMode
    ? {
      present: "rgba(40, 226, 167, 0.18)",
      absent: "rgba(254, 44, 59, 0.18)",
      late: "rgba(255, 227, 184, 0.2)",
      excused: "rgba(38, 151, 162, 0.2)",
    }
    : {
      present: "rgba(40, 226, 167, 0.1)",
      absent: "rgba(254, 44, 59, 0.1)",
      late: "rgba(255, 227, 184, 0.5)",
      excused: "rgba(38, 151, 162, 0.1)",
    } satisfies Record<AttendanceStatus, string>;
  return {
    alignItems: "center",
    backgroundColor: active
      ? activeColors[status].background
      : hovered
        ? hoverBackgrounds[status]
        : "transparent",
    border: `1px solid ${active ? activeColors[status].background : "transparent"}`,
    borderRadius: 6,
    boxSizing: "border-box",
    color: active ? activeColors[status].color : inactiveColors[status],
    display: "flex",
    gap: 8,
    height: 34,
    minHeight: 34,
    padding: "6px 10px",
    textAlign: "left",
    width: "100%",
  };
};

const formatDate = (value: string) => {
  const date = new Date(`${value}T12:00:00.000Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const formatShortNumericDate = (value: string) => {
  const date = new Date(`${value}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${String(date.getUTCFullYear()).slice(-2)}`;
};

const formatCalendarFooterDate = (value: string) => {
  const date = new Date(`${value}T12:00:00.000Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
};

const compactDate = (value: string) => {
  const date = new Date(`${value}T12:00:00.000Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const isoDate = (date: Date) => date.toISOString().slice(0, 10);

const startOfUtcMonth = (value: string) => {
  const date = new Date(`${value}T12:00:00.000Z`);
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  return new Date(Date.UTC(safeDate.getUTCFullYear(), safeDate.getUTCMonth(), 1, 12));
};

const shiftUtcMonth = (date: Date, offset: number) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1, 12));

const calendarGridDates = (monthStart: Date) => {
  const firstVisible = new Date(monthStart);
  firstVisible.setUTCDate(monthStart.getUTCDate() - ((monthStart.getUTCDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, index) => {
    const next = new Date(firstVisible);
    next.setUTCDate(firstVisible.getUTCDate() + index);
    return next;
  });
};

const getWeekDates = (
  schoolDate: string,
  activeWeekdays: readonly number[],
  fallbackDates: readonly string[],
) => {
  const date = new Date(`${schoolDate}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || !activeWeekdays.length) {
    return Array.from(new Set(fallbackDates)).sort();
  }

  const sunday = new Date(date);
  sunday.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return Array.from({ length: 7 }, (_, offset) => {
    const next = new Date(sunday);
    next.setUTCDate(sunday.getUTCDate() + offset);
    return next;
  })
    .filter((next) => activeWeekdays.includes(next.getUTCDay()))
    .map(isoDate);
};

const getRollingSchoolDatesEndingAt = (
  schoolDate: string,
  activeWeekdays: readonly number[],
  targetSchoolDayCount = 30,
) => {
  const end = new Date(`${schoolDate}T12:00:00.000Z`);
  if (Number.isNaN(end.getTime()) || !activeWeekdays.length) {
    return [schoolDate];
  }
  const dates: string[] = [];
  const cursor = new Date(end);
  while (dates.length < targetSchoolDayCount) {
    if (activeWeekdays.includes(cursor.getUTCDay())) {
      dates.push(isoDate(cursor));
    }
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return dates.reverse();
};

const divisionRateColor = (attendanceRatePercent: number) => {
  if (attendanceRatePercent >= 91) return "#28E2A7";
  if (attendanceRatePercent >= 71) return "#FFE3B8";
  return "#FF7A59";
};

const overviewStatDotColor = (label: string) => {
  if (label === "Attendance" || label === "Present") return STATUS_VISUALS.present.dot;
  if (label === "Absent") return STATUS_VISUALS.absent.dot;
  if (label === "Late") return STATUS_VISUALS.late.dot;
  if (label === "Excused") return STATUS_VISUALS.excused.dot;
  return "var(--text-secondary)";
};

const getClassroom = (
  config: AttendanceControlPlaneConfig,
  classroomId: string | null | undefined,
) =>
  config.classrooms.find((classroom) => classroom.classroomId === classroomId) ??
  null;

const getGrade = (
  config: AttendanceControlPlaneConfig,
  gradeId: string | null | undefined,
) => config.grades.find((grade) => grade.gradeId === gradeId) ?? null;

const getGradeForClassroom = (
  config: AttendanceControlPlaneConfig,
  classroomId: string,
) => {
  const classroom = getClassroom(config, classroomId);
  return classroom ? getGrade(config, classroom.gradeId) : null;
};

const stateForClassroom = (
  config: AttendanceControlPlaneConfig,
  classroomId: string,
  current: RecordsFilterState,
  periodId = "",
): RecordsFilterState => {
  const classroom = getClassroom(config, classroomId);
  const grade = classroom ? getGrade(config, classroom.gradeId) : null;
  return {
    ...current,
    divisionId: grade?.divisionId ?? current.divisionId,
    gradeId: grade?.gradeId ?? current.gradeId,
    classroomId: classroom?.classroomId ?? current.classroomId,
    periodId,
  };
};

const getStudentName = (
  config: AttendanceControlPlaneConfig,
  studentId: string,
) =>
  config.rosterMemberships.find((membership) => membership.studentId === studentId)
    ?.studentName ?? studentId;

const formatStudentNameLastFirst = (studentName: string) => {
  const parts = studentName.trim().split(/\s+/);
  if (parts.length < 2) return studentName;
  const lastName = parts[parts.length - 1];
  const firstNames = parts.slice(0, -1).join(" ");
  return `${lastName}, ${firstNames}`;
};

const getStudentInitials = (studentName: string) => {
  const parts = studentName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
};

const getStudentGradeLabel = (
  config: AttendanceControlPlaneConfig,
  studentId: string,
) => {
  const membership = config.rosterMemberships.find((entry) => entry.studentId === studentId);
  if (!membership) return "";
  const classroom = getClassroom(config, membership.classroomId);
  const grade = classroom ? getGrade(config, classroom.gradeId) : null;
  return grade?.label ?? "";
};

const LEGACY_DAILY_PERIOD_ID = "daily";
const isLegacyDailyOnlyPeriodConfig = (config: AttendanceControlPlaneConfig) =>
  config.periods.length === 1 &&
  config.periods[0]?.periodId === LEGACY_DAILY_PERIOD_ID &&
  config.periods[0]?.label.trim().toLowerCase() === "daily";

const displayConfigForRecords = (
  config: AttendanceControlPlaneConfig,
): AttendanceControlPlaneConfig =>
  isLegacyDailyOnlyPeriodConfig(config)
    ? {
        ...config,
        periods: DEFAULT_ATTENDANCE_PERIODS.map((period) => ({ ...period })),
      }
    : config;

const compactPeriodColumnLabel = (label: string) =>
  label.match(/^\s*(\d+)/)?.[1] ?? label;

const cellMatchesDisplayFilter = (
  cell: Pick<AttendanceCellRecord, "classroomId" | "periodId">,
  filter: AttendanceRecordsFilter,
  config: AttendanceControlPlaneConfig,
) => {
  if (filter.classroomId && cell.classroomId !== filter.classroomId) return false;
  if (filter.periodId && cell.periodId !== filter.periodId) return false;
  if (filter.gradeId || filter.divisionId) {
    const grade = getGradeForClassroom(config, cell.classroomId);
    if (filter.gradeId && grade?.gradeId !== filter.gradeId) return false;
    if (filter.divisionId && grade?.divisionId !== filter.divisionId) return false;
  }
  return true;
};

const buildDisplayRecordsGrid = (
  grid: AttendanceRecordsGrid,
  config: AttendanceControlPlaneConfig,
): AttendanceRecordsGrid => {
  if (!config.periods.length || !config.rosterMemberships.length) return grid;

  const schoolDates =
    grid.filter.dayMode === "week"
      ? getWeekDates(
          grid.filter.schoolDate,
          config.schoolCalendar.activeWeekdays,
          grid.cells.map((cell) => cell.schoolDate),
        )
      : [grid.filter.schoolDate];
  const expectedPeriods = grid.filter.periodId
    ? config.periods.filter((period) => period.periodId === grid.filter.periodId)
    : [...config.periods].sort((left, right) => left.sortOrder - right.sortOrder);
  if (!schoolDates.length || !expectedPeriods.length) return grid;

  const existingByKey = new Map(
    grid.cells.map((cell) => [makeCellDomKey(cell), cell]),
  );
  const legacyByScope = new Map(
    grid.cells
      .filter((cell) => cell.periodId === LEGACY_DAILY_PERIOD_ID)
      .map((cell) => [
        `${cell.schoolDate}:${cell.classroomId}:${cell.studentId}`,
        cell,
      ]),
  );
  const firstPeriodId =
    [...config.periods].sort((left, right) => left.sortOrder - right.sortOrder)[0]?.periodId ??
    "";
  const cells: AttendanceCellRecord[] = [];

  for (const schoolDate of schoolDates) {
    const parsedDate = new Date(`${schoolDate}T12:00:00.000Z`);
    if (
      Number.isNaN(parsedDate.getTime()) ||
      !config.schoolCalendar.activeWeekdays.includes(parsedDate.getUTCDay())
    ) {
      continue;
    }
    for (const membership of config.rosterMemberships) {
      for (const period of expectedPeriods) {
        const candidateScope = {
          organizationId: grid.filter.organizationId,
          schoolId: grid.filter.schoolId,
          schoolDate,
          classroomId: membership.classroomId,
          periodId: period.periodId,
          studentId: membership.studentId,
        };
        if (!cellMatchesDisplayFilter(candidateScope, grid.filter, config)) continue;

        const exact = existingByKey.get(
          `${schoolDate}:${membership.classroomId}:${period.periodId}:${membership.studentId}`,
        );
        const legacy =
          period.periodId === firstPeriodId
            ? legacyByScope.get(`${schoolDate}:${membership.classroomId}:${membership.studentId}`)
            : null;
        if (exact) {
          cells.push(exact);
          continue;
        }
        if (legacy) {
          cells.push({
            ...legacy,
            periodId: period.periodId,
            sourceSurface: "seed",
          });
          continue;
        }
        cells.push({
          ...candidateScope,
          status: config.schoolCalendar.defaultStatus,
          note: null,
          version: 0,
          submissionState: "draft",
          submittedAt: null,
          submittedBy: null,
          updatedAt: 0,
          updatedBy: "attendance-ui-display",
          sourceSurface: "seed",
        });
      }
    }
  }

  const sortedCells = cells.sort(
    (left, right) =>
      left.schoolDate.localeCompare(right.schoolDate) ||
      left.classroomId.localeCompare(right.classroomId) ||
      left.studentId.localeCompare(right.studentId) ||
      left.periodId.localeCompare(right.periodId),
  );

  return {
    ...grid,
    cells: sortedCells,
  };
};

const filterToSearch = (filter: AttendanceRecordsFilter) => {
  const params = new URLSearchParams();
  params.set("date", filter.schoolDate);
  params.set("mode", filter.dayMode);
  for (const key of ["divisionId", "gradeId", "classroomId", "periodId"] as const) {
    const value = filter[key];
    if (value) params.set(key, value);
  }
  return params.toString();
};

const deliverExportResult = (result: AttendanceCsvExportResult | AttendanceContextExportResult) => {
  if (typeof document === "undefined") return;

  const link = document.createElement("a");
  link.download = result.fileName;
  if (result.delivery === "download-url") {
    link.href = result.downloadUrl;
  } else {
    const blobType = result.fileName.endsWith(".pdf")
      ? "application/pdf"
      : "text/csv;charset=utf-8";
    const blob = new Blob([result.csvText], { type: blobType });
    link.href = URL.createObjectURL(blob);
    window.setTimeout(() => URL.revokeObjectURL(link.href), 30_000);
  }
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const errorMessageFor = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const ErrorState = ({ message }: { message: string }) => (
  <div className="rounded-[8px] border border-[rgba(254, 44, 59,0.28)] bg-[rgba(254, 44, 59,0.08)] px-6 py-5 text-sm text-[var(--iron-grey)]">
    {message}
  </div>
);

const useAsyncQuery = <T,>(
  loader: () => Promise<T>,
  deps: readonly unknown[],
  initialData: T,
) => {
  const [data, setData] = useState<T>(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);
    void loader()
      .then((next) => {
        if (!active) return;
        setData(next);
        setIsLoading(false);
      })
      .catch((reason) => {
        if (!active) return;
        setError(reason);
        setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [...deps, reloadToken]);

  return {
    data,
    isLoading,
    error,
    reload: () => setReloadToken((current) => current + 1),
  };
};

const useAttendanceContext = (bundle: AttendanceFeatureBundle) =>
  useAsyncQuery<AttendanceCaptureContext>(
    () =>
      bundle.transport.query("getAttendanceCaptureContext", {
        input: {
          ...bundle.runtimeScope,
        },
      }),
    [bundle.transport, bundle.runtimeScope.organizationId, bundle.runtimeScope.schoolId],
    DEFAULT_CONTEXT,
  );

type AttendanceControlPlaneState = {
  config: AttendanceControlPlaneConfig | null;
  auditEvents: AttendanceAuditEvent[];
  importStatus: AttendanceImportStatus | null;
  isLoading: boolean;
  error: string | null;
};

export const AttendanceControlPlanePage = ({
  bundle,
}: {
  bundle: AttendanceFeatureBundle;
}) => {
  const [state, setState] = useState<AttendanceControlPlaneState>({
    config: null,
    auditEvents: [],
    importStatus: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let active = true;
    setState((current) => ({ ...current, isLoading: true, error: null }));
    void Promise.all([
      bundle.transport.query("getAttendanceControlPlaneConfig", {
        input: bundle.runtimeScope,
      }),
      bundle.transport.query("listAttendanceAuditEvents", {
        input: { ...bundle.runtimeScope, limit: 5 },
      }),
      bundle.transport.query("getAttendanceImportStatus", {
        input: { ...bundle.runtimeScope, importId: "latest-attendance-import" },
      }),
    ])
      .then(([config, audit, importStatus]) => {
        if (!active) return;
        setState({
          config,
          auditEvents: audit.events,
          importStatus,
          isLoading: false,
          error: null,
        });
      })
      .catch((reason) => {
        if (!active) return;
        setState((current) => ({
          ...current,
          isLoading: false,
          error:
            reason instanceof Error
              ? reason.message
              : "Unable to load attendance control-plane data.",
        }));
      });

    return () => {
      active = false;
    };
  }, [
    bundle.transport,
    bundle.runtimeScope.organizationId,
    bundle.runtimeScope.schoolId,
  ]);

  const config = state.config;

  return (
    <section
      className="space-y-4"
      data-testid="attendance-control-plane-route"
      data-extracted-from={bundle.extractedFrom}
      data-route-family="attendance-v1.2"
      data-route-ready={state.isLoading ? "false" : "true"}
    >
      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
          Attendance
        </p>
        <h2 className="mt-2 text-xl font-semibold text-irongrey">
          Control Plane
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-text-secondary">
          Super-administrator-only configuration, import status, and audit
          visibility for the Attendance v1.2 records workflow, including
          divisions, classrooms, periods, rosters, teacher assignments, and
          trailing 30-day historic attendance seed data.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-border bg-white px-3 py-2 text-sm font-semibold text-irongrey hover:bg-muted"
          >
            Import Attendance CSV
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-hover"
          >
            Save Configuration
          </button>
        </div>
        {state.error ? (
          <p className="mt-3 rounded-md border border-[#fe2c3b]/30 bg-[#fe2c3b]/10 px-3 py-2 text-sm text-[#9f1239]">
            {state.error}
          </p>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-irongrey">Configuration</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Divisions</dt>
              <dd className="font-semibold text-irongrey">{config?.divisions.length ?? 0}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Classrooms</dt>
              <dd className="font-semibold text-irongrey">{config?.classrooms.length ?? 0}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Periods</dt>
              <dd className="font-semibold text-irongrey">{config?.periods.length ?? 0}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Rosters</dt>
              <dd className="font-semibold text-irongrey">
                {config?.rosterMemberships.length ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Teacher assignments</dt>
              <dd className="font-semibold text-irongrey">
                {config?.classrooms.filter((classroom) => classroom.teacherId || classroom.teacherName).length ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Default</dt>
              <dd className="font-semibold capitalize text-irongrey">
                {config?.schoolCalendar.defaultStatus ?? "present"}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-irongrey">Import Status</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Latest status</dt>
              <dd className="font-semibold capitalize text-irongrey">
                {state.importStatus?.status ?? "pending"}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Processed</dt>
              <dd className="font-semibold text-irongrey">
                {state.importStatus?.processedRows ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Failed</dt>
              <dd className="font-semibold text-irongrey">
                {state.importStatus?.failedRows ?? 0}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Trailing 30-day history</dt>
              <dd className="font-semibold text-irongrey">
                Historic attendance seed ready
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-irongrey">Audit Events</p>
          <div className="mt-4 space-y-3">
            {state.auditEvents.length ? (
              state.auditEvents.map((event) => (
                <div key={event.auditId} className="rounded-md border border-border px-3 py-2">
                  <p className="text-sm font-semibold capitalize text-irongrey">
                    {event.action}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {event.actorId} / {event.sourceSurface}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-text-secondary">No audit events recorded.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const AttendanceShell = ({
  bundle,
  title,
  subtitle,
  actions,
  showPageHeader = true,
  children,
  testId,
}: {
  bundle: AttendanceFeatureBundle;
  title: string;
  subtitle: string;
  actions?: ReactNode;
  showPageHeader?: boolean;
  children: ReactNode;
  testId: string;
}) => {
  const sectionTabs = useMemo<SurfaceTab[]>(
    () => [
      {
        id: "overview",
        label: "Overview",
        to: bundle.routeManifest.overviewPath,
        end: true,
      },
      {
        id: "new-records",
        label: "Records",
        to: bundle.routeManifest.newRecordsPath,
      },
      {
        id: "roster",
        label: "Roster",
        to: bundle.routeManifest.rosterPath,
      },
      {
        id: "reports",
        label: "Reports",
        to: bundle.routeManifest.reportsPath,
      },
      {
        id: "teacher-view",
        label: "Teacher View",
        to: bundle.routeManifest.teacherViewPath,
      },
    ],
    [
      bundle.routeManifest.newRecordsPath,
      bundle.routeManifest.overviewPath,
      bundle.routeManifest.reportsPath,
      bundle.routeManifest.rosterPath,
      bundle.routeManifest.teacherViewPath,
    ],
  );

  return (
    <main
      data-testid={testId}
      data-extracted-from={bundle.extractedFrom}
      data-route-family="attendance-v1.2"
      data-route-ready="true"
      className="source-faithful-shell min-w-0"
      style={{ "--ystnj-source-faithful-max-width": "1180px" } as CSSProperties}
    >
      <div className="source-faithful-container">
        <div
          className="mb-5 flex min-h-14 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[var(--border)] py-2 sm:flex-nowrap sm:py-0"
          data-testid="attendance-section-nav-frame"
        >
          <div className="min-w-0 flex-1">
            <ResponsiveSurfaceTabs
              items={sectionTabs}
              ariaLabel="Attendance sections"
              testId="attendance-section-nav"
              desktopClassName="h-14 items-center gap-6 border-b-0 sm:gap-8"
              mobileClassName="py-2"
              testIds={{
                root: "attendance-section-nav",
                itemPrefix: "attendance-section-nav-item",
                mobileTrigger: "attendance-section-nav-mobile-trigger",
                mobileMenu: "attendance-section-nav-mobile-menu",
                mobileItemPrefix: "attendance-section-nav-mobile-item",
              }}
            />
          </div>
          {actions ? (
            <div
              data-testid="attendance-section-actions"
              className="flex min-w-0 flex-1 flex-wrap items-center justify-start gap-2 sm:flex-none sm:flex-nowrap sm:justify-end"
            >
              {actions}
            </div>
          ) : null}
        </div>
        {showPageHeader ? (
          <div className="mb-5">
            <h2 className="text-[24px] font-semibold text-[var(--iron-grey)]">
              {title}
            </h2>
            <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
              {subtitle}
            </p>
          </div>
        ) : null}
        {children}
      </div>
    </main>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="rounded-[8px] border border-dashed border-[var(--border)] bg-[var(--surface-light)] px-6 py-8 text-center text-sm text-[var(--text-secondary)]">
    {message}
  </div>
);

const ExportDropdown = ({
  onExport,
  onExportPdf,
}: {
  onExport?: () => Promise<void>;
  onExportPdf?: () => Promise<void> | void;
}) => {
  const [label, setLabel] = useState("Export CSV");
  const runExport = (nextLabel: string, action: () => Promise<void> | void) => {
    setLabel(nextLabel);
    void Promise.resolve(action()).finally(() => setLabel("Export CSV"));
  };

  return (
    <details
      data-testid="attendance-export-dropdown"
      className="relative bg-white"
      style={{
        border: "1.5px solid var(--primary)",
        borderRadius: 6,
        boxSizing: "border-box",
        height: 41.5,
        minHeight: 41.5,
      }}
    >
      <summary
        className="flex cursor-pointer list-none items-center gap-2 px-3 text-[13px] font-medium leading-[19.5px] text-[var(--primary)] [&::-webkit-details-marker]:hidden"
        style={{ height: "100%", minHeight: 0 }}
      >
        <Download aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
        {label}
      </summary>
      <div
        role="menu"
        aria-label="Attendance export options"
        className="absolute right-0 z-20 mt-2 min-w-[150px] rounded-[8px] border border-[var(--border)] bg-white p-1 shadow-[0_12px_28px_rgba(36,50,53,0.12)]"
      >
        <button
          type="button"
          role="menuitem"
          disabled={!onExport}
          onClick={() => onExport ? runExport("Preparing CSV", onExport) : undefined}
          className="block w-full rounded-[6px] px-3 py-2 text-left text-sm font-semibold text-[var(--iron-grey)] hover:bg-[var(--muted)]"
        >
          Export CSV
        </button>
        <button
          type="button"
          role="menuitem"
          disabled={!onExportPdf}
          onClick={() => onExportPdf ? runExport("Preparing PDF", onExportPdf) : undefined}
          className="block w-full rounded-[6px] px-3 py-2 text-left text-sm font-semibold text-[var(--iron-grey)] hover:bg-[var(--muted)]"
        >
          Export PDF
        </button>
      </div>
    </details>
  );
};

const OverviewPage = ({ bundle }: { bundle: AttendanceFeatureBundle }) => {
  const context = useAttendanceContext(bundle);
  const [schoolDate, setSchoolDate] = useState(DEFAULT_CONTEXT.currentSchoolDate);
  const [rollingSchoolDayWindow, setRollingSchoolDayWindow] =
    useState<AttendanceOverviewRollingWindow>(30);

  useEffect(() => {
    if (context.data.currentSchoolDate) {
      setSchoolDate(context.data.currentSchoolDate);
    }
  }, [context.data.currentSchoolDate]);

  const overview = useAsyncQuery<AttendanceOverview>(
    () =>
      bundle.transport.query("getAttendanceOverview", {
        input: { ...bundle.runtimeScope, schoolDate, rollingSchoolDayWindow },
      }),
    [
      bundle.transport,
      bundle.runtimeScope.organizationId,
      bundle.runtimeScope.schoolId,
      schoolDate,
      rollingSchoolDayWindow,
    ],
    {
      schoolDate,
      metrics: {
        statusCounts: { present: 0, absent: 0, late: 0, excused: 0 },
        absenceMetricCount: 0,
        lateCount: 0,
        recordedCellCount: 0,
        expectedCellCount: 0,
        attendanceRatePercent: 0,
        excusedRatePercent: 0,
      },
      chronicAbsenceRows: [],
      divisionBreakdown: [],
      notSubmittedRows: [],
    },
  );
  const showBlockingOverviewLoading = false;

  const exportOverview = async () => {
    const result = await bundle.transport.mutate("exportAttendanceRecordsCsv", {
      input: {
        ...bundle.runtimeScope,
        scope: "overview-current-date",
        schoolDate,
        rollingSchoolDayWindow,
        includeNotes: true,
      },
    });
    deliverExportResult(result);
  };

  const exportOverviewPdf = async () => {
    const result = await bundle.transport.mutate("exportAttendanceContext", {
      input: {
        ...bundle.runtimeScope,
        context: "overview",
        format: "pdf",
        schoolDate,
        rollingSchoolDayWindow,
        includeNotes: true,
      },
    });
    deliverExportResult(result);
  };

  return (
    <AttendanceShell
      bundle={bundle}
      title="Attendance Overview"
      subtitle={`Daily attendance command center - ${formatDate(schoolDate)}`}
      testId="attendance-overview-root"
      actions={
        <>
          <label className="sr-only" htmlFor="attendance-overview-window">
            Absence window
          </label>
          <select
            id="attendance-overview-window"
            aria-label="Absence window"
            value={String(rollingSchoolDayWindow)}
            onChange={(event) => {
              const nextWindow = Number(event.target.value);
              setRollingSchoolDayWindow(
                OVERVIEW_WINDOW_OPTIONS.includes(nextWindow as AttendanceOverviewRollingWindow)
                  ? nextWindow as AttendanceOverviewRollingWindow
                  : 30,
              );
            }}
            className="h-10 rounded-[8px] border border-[var(--border)] bg-white px-3 text-sm font-medium text-[var(--iron-grey)]"
          >
            {OVERVIEW_WINDOW_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} days
              </option>
            ))}
          </select>
          <input
            aria-label="Dashboard date"
            type="date"
            value={schoolDate}
            onChange={(event) => setSchoolDate(event.target.value)}
            className="h-10 rounded-[8px] border border-[var(--border)] bg-white px-3 text-sm text-[var(--iron-grey)]"
          />
          <ExportDropdown onExport={exportOverview} onExportPdf={exportOverviewPdf} />
        </>
      }
    >
      {overview.error || context.error ? (
        <ErrorState message={errorMessageFor(overview.error ?? context.error, "Unable to load attendance overview.")} />
      ) : showBlockingOverviewLoading ? (
        <EmptyState message="Loading attendance overview." />
      ) : (
        <div data-testid="attendance-overview-layout-grid" className="grid gap-5 lg:grid-cols-12">
            <section data-testid="attendance-chronic-alerts-card" className="rounded-[8px] border border-[rgba(254,44,59,0.25)] bg-[var(--surface-light)] p-5 shadow-[0_4px_14px_rgba(14,165,177,0.08)] lg:col-span-8">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <AlertTriangle
                    aria-hidden="true"
                    className="h-5 w-5 text-[var(--strawberry)]"
                    strokeWidth={2}
                  />
                  <h3 className="text-[18px] font-semibold text-[var(--iron-grey)]">
                    Chronic Absence Alerts
                  </h3>
                </div>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  Students exceeding the 10% absence threshold in the rolling {rollingSchoolDayWindow}-day window.
                </p>
              </div>
              <span className="rounded-full border border-[rgba(254,44,59,0.20)] bg-[rgba(254,44,59,0.08)] px-3 py-1 text-xs font-semibold text-[var(--strawberry)]">
                &gt;= 10% Absence Rate
              </span>
            </div>
            <div data-testid="attendance-chronic-alerts-table-header" className="mt-5 hidden grid-cols-5 gap-3 border-b border-[var(--border)] bg-[var(--muted)] px-4 py-3 text-xs font-semibold uppercase text-[var(--text-secondary)] md:grid">
              <span>Student</span>
              <span>Grade</span>
              <span>Absence Rate</span>
              <span>Total ({rollingSchoolDayWindow}D)</span>
              <span>Action</span>
            </div>
            <div className="divide-y divide-[var(--border)]">
              {overview.data.chronicAbsenceRows.map((row) => (
                <div
                  key={row.studentId}
                  data-testid="attendance-chronic-alert-row"
                  className="grid gap-2 px-4 py-4 md:grid-cols-5 md:items-center md:gap-3"
                >
                  <div>
                    <p className="font-semibold text-[var(--iron-grey)]">
                      {row.studentName}
                    </p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {row.classroomLabel}
                    </p>
                  </div>
                  <span className="text-sm text-[var(--iron-grey)]">{row.gradeLabel}</span>
                  <span className="text-sm font-semibold text-[var(--strawberry)]">{row.absenceRatePercent}%</span>
                  <span className="text-sm text-[var(--iron-grey)]">{row.absenceMetricCount} days</span>
                  <Link
                    to={row.linkTargetRoute}
                    className="inline-flex min-h-7 items-center text-sm font-semibold text-[var(--primary)] hover:underline"
                  >
                    View History
                  </Link>
                </div>
              ))}
            </div>
          </section>

          <aside data-testid="attendance-overview-aside" className="space-y-5 lg:col-span-4">
            <section data-testid="attendance-division-breakdown-card" className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] shadow-[0_4px_14px_rgba(14,165,177,0.08)]">
              <div className="flex items-center gap-2 border-b border-[var(--border)] px-5 py-4">
                <BarChart3
                  aria-hidden="true"
                  className="h-4 w-4 text-[var(--text-secondary)]"
                  strokeWidth={2}
                />
                <h3 className="text-[16px] font-semibold text-[var(--iron-grey)]">
                  Division Breakdown
                </h3>
              </div>
              <div className="space-y-4 px-5 py-4">
                {overview.data.divisionBreakdown.map((row) => (
                  <div key={row.divisionId}>
                    <div className="mb-1 flex justify-between text-sm">
                      <span className="font-medium text-[var(--iron-grey)]">
                        {row.divisionLabel}
                      </span>
                      <span className="font-medium text-[var(--text-secondary)]">
                        {row.attendanceRatePercent}% ({row.presentLateCount}/{row.expectedCellCount})
                      </span>
                    </div>
                    <div
                      role="progressbar"
                      aria-label={`${row.divisionLabel} attendance rate`}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={row.attendanceRatePercent}
                      className="h-2 overflow-hidden rounded-full bg-[var(--muted)]"
                    >
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${row.attendanceRatePercent}%`,
                          backgroundColor: divisionRateColor(row.attendanceRatePercent),
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section data-testid="attendance-key-stats-card" className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-[0_4px_14px_rgba(14,165,177,0.08)]">
              <div className="grid grid-cols-2 gap-4 border-b border-[var(--border)] pb-4">
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: overviewStatDotColor("Attendance") }} />
                    Attendance
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-[var(--iron-grey)]">
                    {overview.data.metrics.attendanceRatePercent}%
                  </p>
                </div>
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: overviewStatDotColor("Excused") }} />
                    Excused
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-[#1A5A83]">
                    {overview.data.metrics.excusedRatePercent}%
                  </p>
                </div>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-4 border-b border-[var(--border)] pb-4">
                {[
                  ["Present", overview.data.metrics.statusCounts.present],
                  ["Absent", overview.data.metrics.statusCounts.absent],
                  ["Late", overview.data.metrics.statusCounts.late],
                  ["Excused", overview.data.metrics.statusCounts.excused],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: overviewStatDotColor(String(label)) }} />
                      {label}
                    </dt>
                    <dd className="mt-2 text-lg font-medium text-[var(--iron-grey)]">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                  Enrolled
                </span>
                <span className="text-lg font-semibold text-[var(--iron-grey)]">
                  {context.data.config.rosterMemberships.length}
                </span>
              </div>
            </section>

            <section data-testid="attendance-not-submitted-card" className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] shadow-[0_4px_14px_rgba(14,165,177,0.08)]">
              <div className="flex items-center gap-2 border-b border-[var(--border)] px-5 py-4">
                <ClipboardList
                  aria-hidden="true"
                  className="h-4 w-4 text-[var(--text-secondary)]"
                  strokeWidth={2}
                />
                <h3 className="text-[16px] font-semibold text-[var(--iron-grey)]">
                  Not Submitted
                </h3>
                <span className="rounded-full bg-[var(--muted)] px-2 py-0.5 text-xs font-semibold text-[var(--text-secondary)]">
                  {overview.data.notSubmittedRows.length}
                </span>
              </div>
              <div className="divide-y divide-[var(--border)]">
                {overview.data.notSubmittedRows.length ? (
                  overview.data.notSubmittedRows.map((row) => (
                    <Link
                      key={`${row.classroomId}-${row.periodId}`}
                      to={`${bundle.routeManifest.recordsPath}?${filterToSearch(row.routeFilter)}`}
                      data-testid="attendance-not-submitted-entry"
                      className="flex items-center justify-between gap-4 px-5 py-4 text-sm transition-colors hover:bg-[rgba(38,151,162,0.05)]"
                    >
                      <div>
                        <p className="font-medium text-[var(--iron-grey)]">
                          {row.teacherName ?? "Unassigned teacher"}
                        </p>
                        <p className="mt-1 text-xs text-[var(--text-secondary)]">
                          {row.classroomLabel} · {row.periodLabel}
                        </p>
                      </div>
                      <ChevronRight
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 text-[var(--text-secondary)]"
                        strokeWidth={2}
                      />
                    </Link>
                  ))
                ) : (
                  <p className="px-5 py-4 text-sm text-[var(--text-secondary)]">
                    All visible attendance scopes have been submitted.
                  </p>
                )}
              </div>
            </section>
          </aside>
        </div>
      )}
    </AttendanceShell>
  );
};

const emptyAttendanceOverview = (schoolDate: string): AttendanceOverview => ({
  schoolDate,
  metrics: {
    statusCounts: { present: 0, absent: 0, late: 0, excused: 0 },
    absenceMetricCount: 0,
    lateCount: 0,
    recordedCellCount: 0,
    expectedCellCount: 0,
    attendanceRatePercent: 0,
    excusedRatePercent: 0,
  },
  chronicAbsenceRows: [],
  divisionBreakdown: [],
  notSubmittedRows: [],
});

const RosterPage = ({ bundle }: { bundle: AttendanceFeatureBundle }) => {
  const context = useAttendanceContext(bundle);
  const config = context.data.config;
  const rosterRows = config.rosterMemberships.map((membership) => {
    const classroom = getClassroom(config, membership.classroomId);
    const grade = classroom ? getGrade(config, classroom.gradeId) : null;
    const division = config.divisions.find((entry) => entry.divisionId === grade?.divisionId) ?? null;
    return {
      ...membership,
      classroomLabel: classroom?.label ?? membership.classroomId,
      gradeLabel: grade?.label ?? "",
      divisionLabel: division?.label ?? "",
      teacherName: classroom?.teacherName ?? "Unassigned teacher",
    };
  });

  return (
    <AttendanceShell
      bundle={bundle}
      title="Roster"
      subtitle={`${rosterRows.length} active attendance roster memberships`}
      testId="attendance-roster-root"
    >
      {context.error ? (
        <ErrorState message={errorMessageFor(context.error, "Unable to load attendance roster.")} />
      ) : (
        <section
          data-testid="attendance-roster-body"
          className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] shadow-[0_4px_14px_rgba(14,165,177,0.08)]"
        >
          <div className="grid gap-3 border-b border-[var(--border)] px-5 py-4 md:grid-cols-4">
            {[
              ["Students", rosterRows.length],
              ["Classrooms", config.classrooms.length],
              ["Grades", config.grades.length],
              ["Periods", config.periods.length],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{label}</p>
                <p className="mt-1 text-2xl font-semibold text-[var(--iron-grey)]">{value}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-white">
                  {["Student", "Division", "Grade", "Classroom", "Teacher"].map((column) => (
                    <th key={column} className="px-4 py-3 font-semibold text-[var(--iron-grey)]">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rosterRows.map((row) => (
                  <tr key={row.studentId} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="px-4 py-3 font-semibold text-[var(--iron-grey)]">
                      <Link to={bundle.routeManifest.studentHistoryPath.replace(":studentId", encodeURIComponent(row.studentId))} className="text-[var(--primary)] hover:underline">
                        {row.studentName}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{row.divisionLabel}</td>
                    <td className="px-4 py-3">{row.gradeLabel}</td>
                    <td className="px-4 py-3">{row.classroomLabel}</td>
                    <td className="px-4 py-3">{row.teacherName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </AttendanceShell>
  );
};

const ReportsPage = ({ bundle }: { bundle: AttendanceFeatureBundle }) => {
  const [schoolDate, setSchoolDate] = useState(DEFAULT_CONTEXT.currentSchoolDate);
  const rollingSchoolDayWindow: AttendanceOverviewRollingWindow = 30;
  const overview = useAsyncQuery<AttendanceOverview>(
    () =>
      bundle.transport.query("getAttendanceOverview", {
        input: {
          ...bundle.runtimeScope,
          schoolDate,
          rollingSchoolDayWindow,
        },
      }),
    [
      bundle.transport,
      bundle.runtimeScope.organizationId,
      bundle.runtimeScope.schoolId,
      schoolDate,
      rollingSchoolDayWindow,
    ],
    emptyAttendanceOverview(schoolDate),
  );

  return (
    <AttendanceShell
      bundle={bundle}
      title="Reports"
      subtitle={`Attendance report snapshots for ${formatDate(schoolDate)}`}
      testId="attendance-reports-root"
      actions={
        <input
          aria-label="Report date"
          type="date"
          value={schoolDate}
          onChange={(event) => setSchoolDate(event.target.value)}
          className="h-10 rounded-[8px] border border-[var(--border)] bg-white px-3 text-sm text-[var(--iron-grey)]"
        />
      }
    >
      {overview.error ? (
        <ErrorState message={errorMessageFor(overview.error, "Unable to load attendance reports.")} />
      ) : (
        <section data-testid="attendance-reports-body" className="grid gap-5 lg:grid-cols-12">
          <div className="grid gap-5 lg:col-span-8">
            <section className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-[0_4px_14px_rgba(14,165,177,0.08)]">
              <div className="flex items-center gap-2">
                <BarChart3 aria-hidden="true" className="h-5 w-5 text-[var(--primary)]" strokeWidth={2} />
                <h3 className="text-[18px] font-semibold text-[var(--iron-grey)]">Division Report</h3>
              </div>
              <div className="mt-5 space-y-4">
                {overview.data.divisionBreakdown.map((row) => (
                  <div key={row.divisionId}>
                    <div className="mb-1 flex justify-between text-sm">
                      <span className="font-medium text-[var(--iron-grey)]">{row.divisionLabel}</span>
                      <span className="font-medium text-[var(--text-secondary)]">
                        {row.attendanceRatePercent}% attendance
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-[var(--muted)]">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${row.attendanceRatePercent}%`,
                          backgroundColor: divisionRateColor(row.attendanceRatePercent),
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
            <section className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-[0_4px_14px_rgba(14,165,177,0.08)]">
              <div className="flex items-center gap-2">
                <AlertTriangle aria-hidden="true" className="h-5 w-5 text-[var(--strawberry)]" strokeWidth={2} />
                <h3 className="text-[18px] font-semibold text-[var(--iron-grey)]">Chronic Absence Watchlist</h3>
              </div>
              <div className="mt-4 divide-y divide-[var(--border)]">
                {overview.data.chronicAbsenceRows.length ? (
                  overview.data.chronicAbsenceRows.map((row) => (
                    <div key={row.studentId} className="grid gap-2 py-3 md:grid-cols-4 md:items-center">
                      <span className="font-semibold text-[var(--iron-grey)]">{row.studentName}</span>
                      <span className="text-sm text-[var(--text-secondary)]">{row.gradeLabel}</span>
                      <span className="text-sm text-[var(--text-secondary)]">{row.classroomLabel}</span>
                      <span className="text-sm font-semibold text-[var(--strawberry)]">{row.absenceRatePercent}% absence</span>
                    </div>
                  ))
                ) : (
                  <p className="py-3 text-sm text-[var(--text-secondary)]">No chronic absence rows for the current report window.</p>
                )}
              </div>
            </section>
          </div>
          <aside className="space-y-5 lg:col-span-4">
            <section className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-[0_4px_14px_rgba(14,165,177,0.08)]">
              <h3 className="text-[16px] font-semibold text-[var(--iron-grey)]">Daily Summary</h3>
              <dl className="mt-4 grid grid-cols-2 gap-4">
                {[
                  ["Attendance", `${overview.data.metrics.attendanceRatePercent}%`],
                  ["Excused", `${overview.data.metrics.excusedRatePercent}%`],
                  ["Recorded", overview.data.metrics.recordedCellCount],
                  ["Expected", overview.data.metrics.expectedCellCount],
                  ["Absences", overview.data.metrics.absenceMetricCount],
                  ["Late", overview.data.metrics.lateCount],
                ].map(([label, value]) => (
                  <div key={String(label)}>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{label}</dt>
                    <dd className="mt-1 text-lg font-semibold text-[var(--iron-grey)]">{value}</dd>
                  </div>
                ))}
              </dl>
            </section>
            <section className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] shadow-[0_4px_14px_rgba(14,165,177,0.08)]">
              <div className="flex items-center gap-2 border-b border-[var(--border)] px-5 py-4">
                <ClipboardList aria-hidden="true" className="h-4 w-4 text-[var(--text-secondary)]" strokeWidth={2} />
                <h3 className="text-[16px] font-semibold text-[var(--iron-grey)]">Missing Submissions</h3>
              </div>
              <div className="divide-y divide-[var(--border)]">
                {overview.data.notSubmittedRows.length ? (
                  overview.data.notSubmittedRows.slice(0, 6).map((row) => (
                    <Link
                      key={`${row.classroomId}-${row.periodId}`}
                      to={`${bundle.routeManifest.recordsPath}?${filterToSearch(row.routeFilter)}`}
                      className="block px-5 py-3 text-sm hover:bg-[rgba(38,151,162,0.05)]"
                    >
                      <span className="font-medium text-[var(--iron-grey)]">{row.classroomLabel}</span>
                      <span className="ml-2 text-[var(--text-secondary)]">{row.periodLabel}</span>
                    </Link>
                  ))
                ) : (
                  <p className="px-5 py-4 text-sm text-[var(--text-secondary)]">All submissions are in.</p>
                )}
              </div>
            </section>
          </aside>
        </section>
      )}
    </AttendanceShell>
  );
};

type RecordsFilterState = {
  schoolDate: string;
  dayMode: "day" | "week";
  divisionId: string;
  gradeId: string;
  classroomId: string;
  periodId: string;
};

const createFilter = (
  bundle: AttendanceFeatureBundle,
  state: RecordsFilterState,
): AttendanceRecordsFilter => ({
  ...bundle.runtimeScope,
  schoolDate: state.schoolDate,
  dayMode: state.dayMode,
  divisionId: state.divisionId || null,
  gradeId: state.gradeId || null,
  classroomId: state.classroomId || null,
  periodId: state.periodId || null,
});

const parseRecordsFilterSearch = (
  search: string,
  fallbackSchoolDate: string,
): Partial<RecordsFilterState> => {
  const params = new URLSearchParams(search);
  const dayMode = params.get("mode") === "week" ? "week" : "day";
  const schoolDate = params.get("date") || fallbackSchoolDate;
  return {
    schoolDate,
    dayMode,
    divisionId: params.get("divisionId") ?? undefined,
    gradeId: params.get("gradeId") ?? undefined,
    classroomId: params.get("classroomId") ?? undefined,
    periodId: params.get("periodId") ?? undefined,
  };
};

const normalizeRecordsFilterState = (
  config: AttendanceControlPlaneConfig,
  current: RecordsFilterState,
  searchState: Partial<RecordsFilterState>,
  fallbackSchoolDate: string,
  preferredClassroomId?: string | null,
): RecordsFilterState => {
  const requested = { ...current, ...searchState };
  const requestedClassroom = requested.classroomId
    ? config.classrooms.find((classroom) => classroom.classroomId === requested.classroomId)
    : null;
  const preferredClassroom = !requestedClassroom && preferredClassroomId
    ? config.classrooms.find((classroom) => classroom.classroomId === preferredClassroomId)
    : null;
  const anchorClassroom = requestedClassroom ?? preferredClassroom;
  const anchorGrade = anchorClassroom
    ? config.grades.find((grade) => grade.gradeId === anchorClassroom.gradeId)
    : null;
  const divisionId = config.divisions.some(
    (division) => division.divisionId === requested.divisionId,
  )
    ? requested.divisionId
    : anchorGrade?.divisionId ?? config.divisions[0]?.divisionId ?? "";
  const gradeOptions = config.grades.filter(
    (grade) => !divisionId || grade.divisionId === divisionId,
  );
  const gradeId =
    divisionId &&
    gradeOptions.some((grade) => grade.gradeId === requested.gradeId)
      ? requested.gradeId
      : anchorGrade && gradeOptions.some((grade) => grade.gradeId === anchorGrade.gradeId)
        ? anchorGrade.gradeId
        : gradeOptions[0]?.gradeId ?? "";
  const classroomOptions = config.classrooms.filter(
    (classroom) => !gradeId || classroom.gradeId === gradeId,
  );
  const classroomId =
    gradeId &&
    classroomOptions.some(
      (classroom) => classroom.classroomId === requested.classroomId,
    )
      ? requested.classroomId
      : anchorClassroom && classroomOptions.some((classroom) => classroom.classroomId === anchorClassroom.classroomId)
        ? anchorClassroom.classroomId
        : classroomOptions[0]?.classroomId ?? "";
  const periodId =
    classroomId &&
    requested.periodId &&
    config.periods.some((period) => period.periodId === requested.periodId)
      ? requested.periodId
      : "";

  return {
    schoolDate: requested.schoolDate || fallbackSchoolDate,
    dayMode: requested.dayMode === "week" ? "week" : "day",
    divisionId,
    gradeId,
    classroomId,
    periodId,
  };
};

const sameRecordsFilterState = (
  left: RecordsFilterState,
  right: RecordsFilterState,
) =>
  left.schoolDate === right.schoolDate &&
  left.dayMode === right.dayMode &&
  left.divisionId === right.divisionId &&
  left.gradeId === right.gradeId &&
  left.classroomId === right.classroomId &&
  left.periodId === right.periodId;

const useRecordsFilterState = (
  bundle: AttendanceFeatureBundle,
  config: AttendanceControlPlaneConfig,
  schoolDate: string,
  search: string,
  preferredClassroomId?: string | null,
) => {
  const [state, setState] = useState<RecordsFilterState>(() => {
    const searchState = parseRecordsFilterSearch(search, schoolDate);
    return {
      schoolDate: searchState.schoolDate ?? schoolDate,
      dayMode: searchState.dayMode ?? "day",
      divisionId: searchState.divisionId ?? "",
      gradeId: searchState.gradeId ?? "",
      classroomId: searchState.classroomId ?? "",
      periodId: searchState.periodId ?? "",
    };
  });

  useEffect(() => {
    if (
      !config.divisions.length ||
      !config.grades.length ||
      !config.classrooms.length ||
      !config.periods.length
    ) {
      return;
    }
    const searchState = parseRecordsFilterSearch(search, schoolDate);
    setState((current) => {
      const next = normalizeRecordsFilterState(
        config,
        current,
        searchState,
        schoolDate,
        preferredClassroomId,
      );
      return sameRecordsFilterState(current, next) ? current : next;
    });
  }, [config, schoolDate, search, preferredClassroomId]);

  return {
    state,
    setState,
    filter: useMemo(
      () => createFilter(bundle, state),
      [
        bundle.runtimeScope.organizationId,
        bundle.runtimeScope.schoolId,
        state.schoolDate,
        state.dayMode,
        state.divisionId,
        state.gradeId,
        state.classroomId,
        state.periodId,
      ],
    ),
  };
};

type CompactFilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

const CompactFilterSelect = ({
  label,
  value,
  placeholder,
  options,
  disabled = false,
  insetLabel,
  minWidth = 0,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  options: readonly CompactFilterOption[];
  disabled?: boolean;
  insetLabel?: string;
  minWidth?: number;
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const selectedOption = options.find((option) => option.value === value);
  const hasValue = Boolean(selectedOption);
  const firstRealValue =
    options.find((option) => option.value && !option.disabled)?.value ?? "";

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const buttonStyle: CSSProperties = {
    minWidth: minWidth || undefined,
    backgroundColor: hasValue ? "rgba(38,151,162,0.05)" : "#FFFFFF",
    borderColor: open || hasValue ? "rgba(38,151,162,0.4)" : "#E5E7EB",
    boxShadow: "none",
    color: hasValue ? "#404F4D" : "#6B7280",
    outline: "none",
  };

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={open}
        data-value={value}
        data-first-real-value={firstRealValue}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
          }
          if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={`flex h-[37.5px] items-center justify-between gap-2 rounded-[6px] border px-3 py-2 text-[13px] font-medium leading-[19.5px] transition-colors hover:border-[rgba(38,151,162,0.4)] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.18)] ${
          disabled ? "cursor-not-allowed opacity-50 hover:border-[#E5E7EB]" : ""
        }`}
        style={buttonStyle}
      >
        <span className="flex min-w-0 items-center gap-2">
          {insetLabel ? (
            <span className="shrink-0 text-[11px] font-medium uppercase leading-[14.3px] tracking-[0.025em] text-[#9CA3AF]">
              {insetLabel}
            </span>
          ) : null}
          <span className="truncate">{selectedOption?.label ?? placeholder}</span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      {open ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label={`${label} options`}
          className="absolute left-0 top-full z-[60] mt-1 max-h-72 min-w-[200px] overflow-y-auto rounded-[12px] border border-[#E5E7EB] bg-white py-1 shadow-[0_4px_6px_2px_rgba(206,242,237,0.39)]"
        >
          {options.map((option) => {
            const selected = option.value === value;
            const selectOption = () => {
              if (option.disabled) return;
              onChange(option.value);
              setOpen(false);
            };
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                data-value={option.value}
                disabled={option.disabled}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  selectOption();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  selectOption();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectOption();
                  }
                }}
                className={`flex h-[35.5px] w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] leading-[19.5px] text-[#404F4D] transition-colors hover:bg-[#F4F6F3] ${
                  selected ? "bg-[rgba(38,151,162,0.08)] text-[var(--primary)]" : ""
                } ${option.disabled ? "cursor-not-allowed opacity-50" : ""}`}
              >
                <span>{option.label}</span>
                {selected ? (
                  <Check aria-hidden="true" className="h-4 w-4 text-[var(--primary)]" strokeWidth={2} />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const CompactDatePicker = ({
  label = "Date",
  value,
  maxDate = schoolTodayIso(),
  onChange,
}: {
  label?: string;
  value: string;
  maxDate?: string;
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfUtcMonth(value));
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const maxSelectableDate = maxDate || schoolTodayIso();

  useEffect(() => {
    setViewMonth(startOfUtcMonth(value));
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const monthLabel = viewMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const visibleDates = calendarGridDates(viewMonth);
  const canGoNext =
    shiftUtcMonth(viewMonth, 1).getTime() <= startOfUtcMonth(maxSelectableDate).getTime();

  const selectDate = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        aria-label={label}
	        aria-haspopup="dialog"
	        aria-expanded={open}
	        data-value={value}
	        data-max-date={maxSelectableDate}
	        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
          }
          if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className="flex h-[37.5px] min-w-[120px] items-center justify-between gap-2 rounded-[6px] border border-[rgba(38,151,162,0.4)] bg-[rgba(38,151,162,0.05)] px-3 text-[13px] font-medium leading-[19.5px] text-[#404F4D] transition-colors hover:border-[rgba(38,151,162,0.55)] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.18)]"
        style={{
          alignItems: "center",
          backgroundColor: "rgba(38,151,162,0.05)",
          borderColor: "rgba(38,151,162,0.4)",
          borderRadius: 6,
          borderStyle: "solid",
          borderWidth: 1,
          boxShadow: "none",
          display: "flex",
          height: 37.5,
          minHeight: 37.5,
          outline: "none",
          padding: "0 12px",
          width: 120,
        }}
      >
        <span className="flex min-w-0 items-center gap-2">
          <CalendarDays aria-hidden="true" className="h-4 w-4 shrink-0 text-[#94A3B8]" strokeWidth={2} />
          <span>{formatShortNumericDate(value)}</span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={`${label} calendar`}
          className="absolute left-0 top-full z-[70] mt-2 w-[320px] rounded-[12px] border border-[#E5E7EB] bg-white shadow-[0_14px_32px_rgba(36,50,53,0.12)]"
        >
          <div className="flex h-14 items-center justify-between px-4">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setViewMonth((current) => shiftUtcMonth(current, -1))}
              className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#6B7280] hover:bg-[#F4F6F3] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.18)]"
            >
              <ChevronLeft aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
            </button>
            <div className="text-[13px] font-semibold leading-[19.5px] text-[#404F4D]">
              {monthLabel}
            </div>
            <button
              type="button"
              aria-label="Next month"
              disabled={!canGoNext}
              onClick={() => setViewMonth((current) => shiftUtcMonth(current, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#CBD5E1] hover:bg-[#F4F6F3] hover:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.18)] disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#CBD5E1]"
            >
              <ChevronRight aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-y-1 px-4 pb-3 text-center">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
              <div key={day} className="py-2 text-[11px] font-medium leading-[14px] text-[#94A3B8]">
                {day}
              </div>
            ))}
            {visibleDates.map((date) => {
              const iso = isoDate(date);
              const isFuture = iso > maxSelectableDate;
              const isSelected = iso === value && !isFuture;
              const isCurrentMonth =
                date.getUTCMonth() === viewMonth.getUTCMonth()
                && date.getUTCFullYear() === viewMonth.getUTCFullYear();
              const dayButtonStyle: CSSProperties = {
                backgroundColor: isSelected ? "var(--primary)" : "transparent",
                color: isSelected
                  ? "#FFFFFF"
                  : isFuture || !isCurrentMonth
                    ? "#CBD5E1"
                    : "#404F4D",
                cursor: isFuture ? "not-allowed" : "pointer",
                opacity: 1,
              };
              return (
                <button
                  key={iso}
                  type="button"
                  aria-label={date.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    timeZone: "UTC",
                  })}
                  aria-pressed={isSelected}
                  disabled={isFuture}
                  onClick={() => selectDate(iso)}
                  className={`mx-auto flex h-9 w-9 items-center justify-center rounded-[8px] text-[13px] font-medium leading-[19.5px] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.22)] ${
                    isSelected
                      ? "bg-[var(--primary)] text-white"
                      : isFuture
                        ? "cursor-not-allowed text-[#CBD5E1]"
                      : isCurrentMonth
                        ? "text-[#404F4D] hover:bg-[#F4F6F3]"
                        : "text-[#CBD5E1] hover:bg-[#F8FAF9]"
                  }`}
                  style={dayButtonStyle}
                >
                  {date.getUTCDate()}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between border-t border-[#E5E7EB] px-4 py-3 text-[13px] leading-[19.5px]">
            <button
              type="button"
              onClick={() => selectDate(maxSelectableDate)}
              className="font-medium text-[var(--primary)] hover:underline focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.18)]"
            >
              Today
            </button>
            <span className="text-[#94A3B8]">
              {formatCalendarFooterDate(value)}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const FilterControls = ({
  config,
  state,
  setState,
  showClassroomPaging = true,
  showWeekTabs = true,
  showMode = true,
  allowAllPeriods = true,
  classroomPagerIds,
  compactLayout = false,
  compactPager = "previous-next",
  maxDate,
}: {
  config: AttendanceControlPlaneConfig;
  state: RecordsFilterState;
  setState: React.Dispatch<React.SetStateAction<RecordsFilterState>>;
  showClassroomPaging?: boolean;
  showWeekTabs?: boolean;
  showMode?: boolean;
  allowAllPeriods?: boolean;
  classroomPagerIds?: readonly string[];
  compactLayout?: boolean;
  compactPager?: "none" | "next" | "previous-next";
  maxDate?: string;
}) => {
  const gradeOptions = config.grades.filter(
    (grade) => grade.divisionId === state.divisionId,
  );
  const classroomOptions = config.classrooms.filter(
    (classroom) => classroom.gradeId === state.gradeId,
  );
  const periodOptions = [...config.periods].sort(
    (left, right) => left.sortOrder - right.sortOrder,
  );
  const defaultPeriodId = allowAllPeriods ? "" : periodOptions[0]?.periodId ?? "";
  const pagerIdSet = classroomPagerIds?.length ? new Set(classroomPagerIds) : null;
  const pagingClassroomOptions = pagerIdSet
    ? config.classrooms.filter((classroom) => pagerIdSet.has(classroom.classroomId))
    : classroomOptions;

  const currentClassroomIndex = pagingClassroomOptions.findIndex(
    (classroom) => classroom.classroomId === state.classroomId,
  );
  const moveClassroom = (direction: -1 | 1) => {
    if (!pagingClassroomOptions.length) return;
    const currentIndex = currentClassroomIndex >= 0 ? currentClassroomIndex : 0;
    const next =
      pagingClassroomOptions[(currentIndex + direction + pagingClassroomOptions.length) % pagingClassroomOptions.length];
    setState((current) => stateForClassroom(config, next.classroomId, current, defaultPeriodId));
  };
  const weekDates = getWeekDates(
    state.schoolDate,
    config.schoolCalendar.activeWeekdays,
    [state.schoolDate],
  );
  const labelClassName = (minWidthClass: string) =>
    compactLayout ? "w-auto" : minWidthClass;
  const labelStyle = (minWidth: number) =>
    compactLayout ? undefined : { minWidth };
  const controlHeightClass = compactLayout ? "h-[38px]" : "h-11";
  const controlTextClass = compactLayout
    ? "text-[13px] leading-[19.5px]"
    : "text-sm leading-normal";
  const selectClassName = (selected: boolean, disabled = false, hasInsetLabel = false) =>
    `${controlHeightClass} ${compactLayout ? "w-auto" : "w-full"} appearance-none rounded-[6px] border py-2 ${hasInsetLabel ? "pl-[64px]" : "pl-3"} pr-9 ${controlTextClass} font-medium transition-colors hover:border-[rgba(38,151,162,0.55)] ${
      selected
        ? "border-[rgba(38,151,162,0.4)] bg-[rgba(38,151,162,0.05)] text-[var(--iron-grey)]"
        : "border-[#E5E7EB] bg-white text-[#9CA3AF]"
    } ${disabled ? "cursor-not-allowed border-[#E5E7EB] bg-white text-[#9CA3AF] opacity-50" : ""}`;
  const selectStyle = (selected: boolean, disabled = false): CSSProperties => {
    if (disabled) {
      return {
        backgroundColor: "#FFFFFF",
        borderColor: "#E5E7EB",
        color: "#9CA3AF",
        opacity: 0.5,
      };
    }
    if (selected) {
      return {
        backgroundColor: "rgba(38,151,162,0.05)",
        borderColor: "rgba(38,151,162,0.4)",
        color: "#404F4D",
      };
    }
    return {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
    };
  };
  const periodSelectClassName = (selected: boolean, disabled = false) =>
    selectClassName(selected, disabled, selected).replace(
      compactLayout ? "w-auto" : "w-full",
      "w-full",
    );
  const renderSelectChevron = () => (
    <ChevronDown
      aria-hidden="true"
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]"
      strokeWidth={2}
    />
  );
  const hierarchySeparator = (
    <span
      aria-hidden="true"
      className="hidden text-[13px] leading-[19.5px] text-[#9CA3AF] sm:block"
    >
      ›
    </span>
  );
  const modeTabs = showMode ? (
    <div
      aria-label="Mode"
      className="flex h-[37.5px] items-center rounded-[6px] border border-[#E5E7EB] bg-[#F4F6F3] p-1"
      style={{ height: 37.5, minHeight: 37.5, padding: 3 }}
    >
      {(["day", "week"] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          aria-pressed={state.dayMode === mode}
          onClick={() =>
            setState((current) => ({
              ...current,
              dayMode: mode,
            }))
          }
          className={`h-[29.5px] rounded-[6px] px-3 text-[13px] font-medium leading-[19.5px] transition-colors ${
            state.dayMode === mode
              ? "bg-white text-[#404F4D] shadow-sm"
              : "text-[#6B7280] hover:text-[#404F4D]"
          }`}
          style={{ height: 31.5, minHeight: 31.5 }}
        >
          {mode === "day" ? "Day" : "Week"}
        </button>
      ))}
    </div>
  ) : null;

  if (compactLayout) {
    const divisionOptions = config.divisions.map((division) => ({
      value: division.divisionId,
      label: division.label,
    }));
    const compactGradeOptions = gradeOptions.map((grade) => ({
      value: grade.gradeId,
      label: grade.label,
    }));
    const compactClassroomOptions = classroomOptions.map((classroom) => ({
      value: classroom.classroomId,
      label: `${classroom.label}${classroom.teacherName ? ` / ${classroom.teacherName}` : ""}`,
    }));
    const compactPeriodOptions = [
      ...(allowAllPeriods ? [{ value: "", label: "All Periods" }] : []),
      ...periodOptions.map((period) => ({
        value: period.periodId,
        label: period.label,
      })),
    ];

    return (
      <section className="mb-5 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
            <label className="w-auto">
              <span className="sr-only">Division</span>
              <CompactFilterSelect
                label="Division"
                value={state.divisionId}
                placeholder="Select division..."
                options={divisionOptions}
                onChange={(divisionId) => {
                  const nextGrade = config.grades.find(
                    (grade) => grade.divisionId === divisionId,
                  );
                  const nextClassroom = nextGrade
                    ? config.classrooms.find(
                        (classroom) => classroom.gradeId === nextGrade.gradeId,
                      )
                    : null;
                  setState((current) => ({
                    ...current,
                    divisionId,
                    gradeId: nextGrade?.gradeId ?? "",
                    classroomId: nextClassroom?.classroomId ?? "",
                    periodId: nextClassroom ? defaultPeriodId : "",
                  }));
                }}
              />
            </label>
            {hierarchySeparator}
            <label className="w-auto">
              <span className="sr-only">Grade</span>
              <CompactFilterSelect
                label="Grade"
                value={state.gradeId}
                placeholder="Select grade..."
                options={compactGradeOptions}
                disabled={!state.divisionId}
                minWidth={132}
                onChange={(gradeId) => {
                  const nextClassroom = config.classrooms.find(
                    (classroom) => classroom.gradeId === gradeId,
                  );
                  setState((current) => ({
                    ...current,
                    gradeId,
                    classroomId: nextClassroom?.classroomId ?? "",
                    periodId: nextClassroom ? defaultPeriodId : "",
                  }));
                }}
              />
            </label>
            {hierarchySeparator}
            <label className="w-auto">
              <span className="sr-only">Classroom</span>
              <CompactFilterSelect
                label="Classroom"
                value={state.classroomId}
                placeholder="Select classroom..."
                options={compactClassroomOptions}
                disabled={!state.gradeId}
                minWidth={160}
                onChange={(classroomId) =>
                  setState((current) => ({
                    ...current,
                    classroomId,
                    periodId: classroomId ? defaultPeriodId : "",
                  }))
                }
              />
            </label>
            {hierarchySeparator}
            <label className="w-auto">
              <span className="sr-only">Period</span>
              <CompactFilterSelect
                label="Period"
                value={state.periodId}
                placeholder="Select period..."
                options={compactPeriodOptions}
                disabled={!state.classroomId}
                insetLabel="Period"
                minWidth={170}
                onChange={(periodId) =>
                  setState((current) => ({ ...current, periodId }))
                }
              />
            </label>
            {showClassroomPaging && compactPager !== "none" ? (
              <div className="flex flex-wrap gap-2">
                {compactPager === "previous-next" ? (
                  <button
                    type="button"
                    onClick={() => moveClassroom(-1)}
                    className="h-[37.5px] rounded-[6px] border border-[#E5E7EB] bg-white px-3 text-[13px] font-medium leading-[19.5px] text-[#6B7280] hover:border-[rgba(38,151,162,0.4)] hover:text-[#404F4D]"
                    style={{ height: 37.5, minHeight: 37.5 }}
                  >
                    Previous Classroom
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => moveClassroom(1)}
                  className="inline-flex h-[37.5px] items-center gap-1 rounded-[6px] border border-[#E5E7EB] bg-white px-2.5 text-[13px] font-medium leading-[19.5px] text-[#6B7280] hover:border-[rgba(38,151,162,0.4)] hover:text-[#404F4D]"
                  style={{ height: 37.5, minHeight: 37.5 }}
                >
                  Next
                  <ChevronRight aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            ) : null}
            {modeTabs}
            <CompactDatePicker
              value={state.schoolDate}
              maxDate={maxDate}
              onChange={(schoolDate) =>
                setState((current) => ({ ...current, schoolDate }))
              }
            />
        </div>
        {showWeekTabs && state.dayMode === "week" ? (
          <div
            data-testid="attendance-week-day-tabs"
            role="tablist"
            aria-label="Week day tabs"
            className="flex gap-2 overflow-x-auto rounded-[8px] border border-[var(--border)] bg-[var(--muted)] p-1"
          >
            {weekDates.map((schoolDate) => (
              <button
                key={schoolDate}
                type="button"
                role="tab"
                aria-selected={state.schoolDate === schoolDate}
                onClick={() => setState((current) => ({ ...current, schoolDate }))}
                className={`min-h-9 shrink-0 rounded-[6px] border px-3 text-[13px] font-medium leading-[19.5px] ${
                  state.schoolDate === schoolDate
                    ? "border-white bg-white text-[var(--iron-grey)] shadow-sm"
                    : "border-transparent text-[var(--text-secondary)] hover:text-[var(--iron-grey)]"
                }`}
              >
                {compactDate(schoolDate)}
              </button>
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className="mb-5 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className={labelClassName("min-w-[190px]")} style={labelStyle(190)}>
          <span className="sr-only">Division</span>
          <span className="relative block">
            <select
              aria-label="Division"
              value={state.divisionId}
              onChange={(event) => {
                const divisionId = event.target.value;
                const nextGrade = config.grades.find(
                  (grade) => grade.divisionId === divisionId,
                );
                const nextClassroom = nextGrade
                  ? config.classrooms.find(
                      (classroom) => classroom.gradeId === nextGrade.gradeId,
                    )
                  : null;
                setState((current) => ({
                  ...current,
                  divisionId,
                  gradeId: nextGrade?.gradeId ?? "",
                  classroomId: nextClassroom?.classroomId ?? "",
                  periodId: nextClassroom ? defaultPeriodId : "",
                }));
              }}
              className={selectClassName(Boolean(state.divisionId))}
              style={selectStyle(Boolean(state.divisionId))}
            >
              <option value="">Select division</option>
              {config.divisions.map((division) => (
                <option key={division.divisionId} value={division.divisionId}>
                  {division.label}
                </option>
              ))}
            </select>
            {renderSelectChevron()}
          </span>
        </label>
        {hierarchySeparator}
        <label className={labelClassName("min-w-[170px]")} style={labelStyle(170)}>
          <span className="sr-only">Grade</span>
          <span className="relative block">
            <select
              aria-label="Grade"
              value={state.gradeId}
              disabled={!state.divisionId}
              onChange={(event) => {
                const gradeId = event.target.value;
                const nextClassroom = config.classrooms.find(
                  (classroom) => classroom.gradeId === gradeId,
                );
                setState((current) => ({
                  ...current,
                  gradeId,
                  classroomId: nextClassroom?.classroomId ?? "",
                  periodId: nextClassroom ? defaultPeriodId : "",
                }));
              }}
              className={selectClassName(Boolean(state.gradeId), !state.divisionId)}
              style={selectStyle(Boolean(state.gradeId), !state.divisionId)}
            >
              <option value="">Select grade</option>
              {gradeOptions.map((grade) => (
                <option key={grade.gradeId} value={grade.gradeId}>
                  {grade.label}
                </option>
              ))}
            </select>
            {renderSelectChevron()}
          </span>
        </label>
        {hierarchySeparator}
        <label className={labelClassName("min-w-[220px]")} style={labelStyle(220)}>
          <span className="sr-only">Classroom</span>
          <span className="relative block">
            <select
              aria-label="Classroom"
              value={state.classroomId}
              disabled={!state.gradeId}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  classroomId: event.target.value,
                  periodId: event.target.value ? defaultPeriodId : "",
                }))
              }
              className={selectClassName(Boolean(state.classroomId), !state.gradeId)}
              style={selectStyle(Boolean(state.classroomId), !state.gradeId)}
            >
              <option value="">Select classroom</option>
              {classroomOptions.map((classroom) => (
                <option key={classroom.classroomId} value={classroom.classroomId}>
                  {classroom.label}
                  {classroom.teacherName ? ` / ${classroom.teacherName}` : ""}
                </option>
              ))}
            </select>
            {renderSelectChevron()}
          </span>
        </label>
        {hierarchySeparator}
        <label
          className={compactLayout ? "min-w-[170px]" : "min-w-[190px]"}
          style={labelStyle(190)}
        >
          <span className="sr-only">Period</span>
          <span className="relative block">
            {state.periodId ? (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] font-medium uppercase leading-[14.3px] tracking-[0.025em] text-[#9CA3AF]"
              >
                Period
              </span>
            ) : null}
            <select
              aria-label="Period"
              value={state.periodId}
              disabled={!state.classroomId}
              onChange={(event) =>
                setState((current) => ({ ...current, periodId: event.target.value }))
              }
              className={periodSelectClassName(Boolean(state.periodId), !state.classroomId)}
              style={{
                ...selectStyle(Boolean(state.periodId), !state.classroomId),
                paddingLeft: state.periodId ? 64 : 12,
              }}
            >
              {allowAllPeriods ? <option value="">All Periods</option> : null}
              {periodOptions.map((period) => (
                <option key={period.periodId} value={period.periodId}>
                  {period.label}
                </option>
              ))}
            </select>
            {renderSelectChevron()}
          </span>
        </label>
      {showClassroomPaging ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => moveClassroom(-1)}
            className="min-h-11 rounded-[8px] border border-[var(--border)] bg-white px-3 text-sm font-semibold text-[var(--iron-grey)] hover:border-[var(--primary)]/40"
          >
            Previous Classroom
          </button>
          <button
            type="button"
            onClick={() => moveClassroom(1)}
            className="min-h-11 rounded-[8px] border border-[var(--border)] bg-white px-3 text-sm font-semibold text-[var(--iron-grey)] hover:border-[var(--primary)]/40"
          >
            Next Classroom
          </button>
        </div>
      ) : null}
      {showMode ? (
        <label className="min-w-[150px]" style={{ minWidth: 150 }}>
          <span className="sr-only">Mode</span>
          <span className="relative block">
            <select
              aria-label="Mode"
              value={state.dayMode}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  dayMode: event.target.value as RecordsFilterState["dayMode"],
                }))
              }
              className={`${controlHeightClass} w-full appearance-none rounded-[6px] border border-[#E5E7EB] bg-white px-3 pr-9 ${controlTextClass} font-medium text-[var(--iron-grey)] hover:border-[rgba(38,151,162,0.55)]`}
              style={{ borderColor: "#E5E7EB", color: "#404F4D" }}
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
            </select>
            {renderSelectChevron()}
          </span>
        </label>
      ) : null}
      <label
        className={compactLayout ? "ml-auto w-auto" : "min-w-[170px]"}
        style={labelStyle(170)}
      >
        <span className="sr-only">Date</span>
        <input
          type="date"
          aria-label="Date"
          value={state.schoolDate}
          onChange={(event) =>
            setState((current) => ({ ...current, schoolDate: event.target.value }))
          }
          className={`${controlHeightClass} ${compactLayout ? "w-auto" : "w-full"} rounded-[6px] border border-[#E5E7EB] bg-white px-3 ${controlTextClass} font-medium text-[var(--iron-grey)] hover:border-[rgba(38,151,162,0.55)]`}
        />
      </label>
      </div>
      {showWeekTabs && state.dayMode === "week" ? (
        <div
          data-testid="attendance-week-day-tabs"
          role="tablist"
          aria-label="Week day tabs"
          className="flex gap-2 overflow-x-auto rounded-[8px] border border-[var(--border)] bg-[var(--muted)] p-1"
        >
          {weekDates.map((schoolDate) => (
            <button
              key={schoolDate}
              type="button"
              role="tab"
              aria-selected={state.schoolDate === schoolDate}
              onClick={() => setState((current) => ({ ...current, schoolDate }))}
              className={`min-h-11 shrink-0 rounded-[7px] border px-3 text-sm font-semibold ${
                state.schoolDate === schoolDate
                  ? "border-white bg-white text-[var(--iron-grey)] shadow-sm"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--iron-grey)]"
              }`}
            >
              {compactDate(schoolDate)}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
};

type GridColumn = {
  id: string;
  label: string;
  summaryLabel: string;
  ariaLabel: string;
  schoolDate?: string;
  subLabel?: string;
  actionScope: {
    columnSchoolDate?: string | null;
    columnPeriodId: string;
  };
  matcher: (cell: AttendanceCellRecord) => boolean;
};

const buildGridColumns = (
  grid: AttendanceRecordsGrid,
  config: AttendanceControlPlaneConfig,
): GridColumn[] => {
  const selectedPeriods = grid.filter.periodId
    ? config.periods.filter((period) => period.periodId === grid.filter.periodId)
    : [...config.periods].sort((left, right) => left.sortOrder - right.sortOrder);

  if (grid.filter.dayMode === "week") {
    const weekDates = getWeekDates(
      grid.filter.schoolDate,
      config.schoolCalendar.activeWeekdays,
      grid.cells.map((cell) => cell.schoolDate),
    );
    return weekDates.flatMap((schoolDate) =>
      selectedPeriods.map((period) => ({
        id: `${schoolDate}:${period.periodId}`,
        label: compactDate(schoolDate),
        schoolDate,
        subLabel: compactPeriodColumnLabel(period.label),
        summaryLabel: `${compactDate(schoolDate)} · Period ${compactPeriodColumnLabel(period.label)} (${period.label})`,
        ariaLabel: `Select ${formatDate(schoolDate)} period ${compactPeriodColumnLabel(period.label)} (${period.label})`,
        actionScope: {
          columnSchoolDate: schoolDate,
          columnPeriodId: period.periodId,
        },
        matcher: (cell) =>
          cell.schoolDate === schoolDate && cell.periodId === period.periodId,
      })),
    );
  }

  return selectedPeriods.map((period) => ({
    id: period.periodId,
    label: compactPeriodColumnLabel(period.label),
    summaryLabel: `Period ${compactPeriodColumnLabel(period.label)} (${period.label})`,
    ariaLabel: `Select period ${compactPeriodColumnLabel(period.label)} (${period.label})`,
    actionScope: {
      columnPeriodId: period.periodId,
    },
    matcher: (cell) => cell.periodId === period.periodId,
  }));
};

type StudentRecordRow = {
  studentId: string;
  cells: AttendanceCellRecord[];
};

const groupCellsByStudent = (grid: AttendanceRecordsGrid): StudentRecordRow[] =>
  Array.from(new Set(grid.cells.map((cell) => cell.studentId))).map((studentId) => ({
    studentId,
    cells: grid.cells.filter((cell) => cell.studentId === studentId),
  }));

const RecordsActionsMenu = ({
  rows,
  columns,
  selectedStudentId,
  selectedColumnId,
  selectedStudentLabel,
  selectedColumnLabel,
  isBusy,
  onMarkRowPresent,
  onResetRow,
  onMarkColumnPresent,
  onResetColumn,
  onMarkVisiblePresent,
  onResetVisible,
}: {
  rows: StudentRecordRow[];
  columns: GridColumn[];
  selectedStudentId: string;
  selectedColumnId: string;
  selectedStudentLabel: string;
  selectedColumnLabel: string;
  isBusy: boolean;
  onMarkRowPresent: (studentId: string) => void;
  onResetRow: (studentId: string) => void;
  onMarkColumnPresent: (columnId: string) => void;
  onResetColumn: (columnId: string) => void;
  onMarkVisiblePresent: () => void;
  onResetVisible: () => void;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobileActionsMenu = useMediaQuery("(max-width: 639px)");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuWasOpenRef = useRef(false);
  const scopeGuidanceId = useId();
  const selectedSummary = [selectedStudentLabel, selectedColumnLabel].filter(Boolean).join(" · ");
  const hasSelectedStudent = Boolean(selectedStudentId);
  const hasSelectedColumn = Boolean(selectedColumnId);
  const scopeGuidance = !hasSelectedStudent && !hasSelectedColumn
    ? "Select a student row or period header to enable scoped actions."
    : hasSelectedStudent && hasSelectedColumn
      ? "Scoped actions will use the selected student and period."
      : hasSelectedStudent
        ? "Student actions enabled. Select a period header to enable period actions."
        : "Period actions enabled. Select a student row to enable student actions.";

  const runAction = (action: () => void) => {
    action();
    setMenuOpen(false);
  };
  const focusActionButton = (placement: "first" | "last" | "next" | "previous") => {
    const buttons = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('button[data-action-item="true"]:not(:disabled)') ?? [],
    );
    if (!buttons.length) return;
    const currentIndex = buttons.findIndex((button) => button === document.activeElement);
    if (placement === "first") {
      buttons[0]?.focus();
      return;
    }
    if (placement === "last") {
      buttons[buttons.length - 1]?.focus();
      return;
    }
    const fallbackIndex = placement === "next" ? -1 : 0;
    const baseIndex = currentIndex >= 0 ? currentIndex : fallbackIndex;
    const offset = placement === "next" ? 1 : -1;
    const nextIndex = (baseIndex + offset + buttons.length) % buttons.length;
    buttons[nextIndex]?.focus();
  };

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    requestAnimationFrame(() => {
      menuRef.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
      focusActionButton("first");
    });
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setMenuOpen(false);
      }
    };
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [menuOpen, rows.length, columns.length]);

  useEffect(() => {
    if (menuOpen) {
      menuWasOpenRef.current = true;
      return;
    }
    if (!menuWasOpenRef.current) return;
    menuWasOpenRef.current = false;
    triggerRef.current?.focus();
  }, [menuOpen]);

  const actionButtonClassName =
    "flex min-h-11 w-full items-center rounded-[6px] px-3 py-2 text-left text-[13px] font-medium leading-[19.5px] text-[#404F4D] transition-colors hover:bg-[rgba(38,151,162,0.08)] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.35)] disabled:cursor-not-allowed disabled:text-[#9CA3AF] disabled:opacity-60 disabled:hover:bg-transparent sm:min-h-[38px] sm:py-1.5";
  const actionButtonStyle: CSSProperties = { minHeight: 40 };

  return (
    <div className="relative mb-4 w-full sm:w-fit">
      <button
        type="button"
        ref={triggerRef}
        data-testid="attendance-bulk-actions-trigger"
        aria-label="Bulk attendance actions"
        aria-haspopup="dialog"
        aria-expanded={menuOpen}
        onClick={() => {
          setMenuOpen((current) => !current);
        }}
        className="flex h-[37.5px] items-center gap-2 rounded-[6px] border border-[#E5E7EB] bg-white px-3 text-[13px] font-medium leading-[19.5px] text-[#6B7280] hover:border-[rgba(38,151,162,0.4)] hover:text-[#404F4D]"
        style={{ height: 37.5, minHeight: 37.5 }}
      >
        <Wand2 aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
        Actions
      </button>
      {menuOpen ? (
        <div
          ref={menuRef}
          data-testid="attendance-bulk-actions-menu"
          role="dialog"
          aria-label="Bulk attendance actions"
          aria-modal="false"
          aria-describedby={scopeGuidanceId}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setMenuOpen(false);
              return;
            }
            if (event.key === "ArrowDown") {
              event.preventDefault();
              focusActionButton("next");
              return;
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              focusActionButton("previous");
              return;
            }
            if (event.key === "Home") {
              event.preventDefault();
              focusActionButton("first");
              return;
            }
            if (event.key === "End") {
              event.preventDefault();
              focusActionButton("last");
            }
          }}
          className="relative z-20 mt-2 overflow-y-auto rounded-[10px] border border-[var(--border)] bg-white py-1 shadow-[0_4px_6px_2px_rgba(206,242,237,0.39)] outline-none"
          style={{
            boxSizing: "border-box",
            width: isMobileActionsMenu ? "100%" : 300,
            maxHeight: "min(420px, calc(100vh - 96px))",
          }}
        >
          <div className="border-b border-[var(--border)] px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              Scope
            </p>
            <p className="mt-1 text-[13px] font-medium leading-[19.5px] text-[#404F4D]">
              {selectedSummary ? `Selected: ${selectedSummary}` : "No cell selected"}
            </p>
            <p id={scopeGuidanceId} className="mt-1 text-xs leading-5 text-[var(--text-secondary)]">
              {scopeGuidance}
            </p>
          </div>
          <div className="px-1 py-1">
            <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              Mark Present
            </p>
            <button
              type="button"
              data-action-item="true"
              disabled={isBusy}
              onClick={() => runAction(onMarkVisiblePresent)}
              className={actionButtonClassName}
              style={actionButtonStyle}
            >
              Mark Visible Records Present
            </button>
            <button
              type="button"
              data-action-item="true"
              disabled={isBusy || !selectedStudentId}
              aria-describedby={!selectedStudentId ? scopeGuidanceId : undefined}
              onClick={() => runAction(() => onMarkRowPresent(selectedStudentId))}
              className={actionButtonClassName}
              style={actionButtonStyle}
            >
              Mark Selected Student Present
            </button>
            <button
              type="button"
              data-action-item="true"
              disabled={isBusy || !selectedColumnId}
              aria-describedby={!selectedColumnId ? scopeGuidanceId : undefined}
              onClick={() => runAction(() => onMarkColumnPresent(selectedColumnId))}
              className={actionButtonClassName}
              style={actionButtonStyle}
            >
              Mark Selected Period Present
            </button>
          </div>
          <div className="border-t border-[var(--border)] px-1 py-1">
            <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              Reset
            </p>
            <p className="px-2 pb-1 text-xs leading-5 text-[var(--text-secondary)]">
              Restores default status. Notes are kept.
            </p>
            <button
              type="button"
              data-action-item="true"
              disabled={isBusy || !selectedStudentId}
              aria-describedby={!selectedStudentId ? scopeGuidanceId : undefined}
              onClick={() => runAction(() => onResetRow(selectedStudentId))}
              className={actionButtonClassName}
              style={actionButtonStyle}
            >
              Reset Selected Student To Default
            </button>
            <button
              type="button"
              data-action-item="true"
              disabled={isBusy || !selectedColumnId}
              aria-describedby={!selectedColumnId ? scopeGuidanceId : undefined}
              onClick={() => runAction(() => onResetColumn(selectedColumnId))}
              className={actionButtonClassName}
              style={actionButtonStyle}
            >
              Reset Selected Period To Default
            </button>
            <button
              type="button"
              data-action-item="true"
              disabled={isBusy}
              onClick={() => runAction(onResetVisible)}
              className={actionButtonClassName}
              style={actionButtonStyle}
            >
              Reset Visible Records To Default
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const RecordsSubmissionActions = ({
  isBusy,
  submitDisabled,
  onSubmit,
  onSubmitNext,
}: {
  isBusy: boolean;
  submitDisabled: boolean;
  onSubmit: () => void;
  onSubmitNext: () => void;
}) => (
  <div aria-label="Record submission actions" className="flex flex-wrap items-center gap-2">
    <button
      type="button"
      disabled={isBusy || submitDisabled}
      onClick={onSubmit}
      className="h-[41.5px] rounded-[6px] border-[1.5px] border-[var(--primary)] bg-white px-3 text-[13px] font-medium leading-[19.5px] text-[var(--primary)] transition-colors hover:bg-[rgba(38,151,162,0.06)] disabled:cursor-not-allowed disabled:opacity-60"
      style={{ height: 41.5, minHeight: 41.5 }}
    >
      Submit
    </button>
    <button
      type="button"
      disabled={isBusy || submitDisabled}
      onClick={onSubmitNext}
      className="h-[39.5px] rounded-[6px] bg-[var(--primary)] px-3 text-[13px] font-medium leading-[19.5px] text-white shadow-sm transition-colors hover:bg-[var(--jungle)] disabled:cursor-not-allowed disabled:opacity-60"
      style={{ height: 39.5, minHeight: 39.5 }}
    >
      Submit & Next
    </button>
  </div>
);

const RecordsPage = ({ bundle }: { bundle: AttendanceFeatureBundle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isNarrowRecordsViewport = useIsNarrowViewport(767);
  const context = useAttendanceContext(bundle);
  const { state, setState, filter } = useRecordsFilterState(
    bundle,
    context.data.config,
    context.data.currentSchoolDate,
    location.search,
    context.data.firstUnrecordedClassroomId,
  );
  const unrecordedClassroomIds =
    context.data.unrecordedClassroomIds.length > 0
      ? context.data.unrecordedClassroomIds
      : context.data.firstUnrecordedClassroomId
        ? [context.data.firstUnrecordedClassroomId]
        : [];
  const records = useAsyncQuery<AttendanceRecordsGrid>(
    () => bundle.transport.query("listAttendanceRecords", { input: filter }),
    [
      bundle.transport,
      filter.organizationId,
      filter.schoolId,
      filter.schoolDate,
      filter.dayMode,
      filter.divisionId,
      filter.gradeId,
      filter.classroomId,
      filter.periodId,
    ],
    { filter, cells: [], studentLinks: [], overwriteWarning: null },
  );
  const displayConfig = useMemo(
    () => displayConfigForRecords(context.data.config),
    [context.data.config],
  );
  const baseDisplayRecords = useMemo(
    () => buildDisplayRecordsGrid(records.data, displayConfig),
    [records.data, displayConfig],
  );
  const [optimisticCells, setOptimisticCells] = useState<Record<string, AttendanceCellRecord>>({});
  const cellSaveQueuesRef = useRef<Record<string, Promise<AttendanceCellRecord>>>({});
  const displayRecords = useMemo(() => {
    if (!Object.keys(optimisticCells).length) return baseDisplayRecords;
    return {
      ...baseDisplayRecords,
      cells: baseDisplayRecords.cells.map((cell) => (
        optimisticCells[makeCellDomKey(cell)] ?? cell
      )),
    };
  }, [baseDisplayRecords, optimisticCells]);
  const columns = useMemo(
    () => buildGridColumns(displayRecords, displayConfig),
    [displayRecords, displayConfig],
  );
  const visibleColumns = useMemo(
    () => (
      state.dayMode === "week" && isNarrowRecordsViewport
        ? columns.filter((column) => !column.schoolDate || column.schoolDate === state.schoolDate)
        : columns
    ),
    [columns, isNarrowRecordsViewport, state.dayMode, state.schoolDate],
  );
  const weekDayGroups = state.dayMode === "week"
    ? Array.from(new Set(columns.map((column) => column.schoolDate).filter(Boolean))) as string[]
    : [];
  const rows = useMemo(() => groupCellsByStudent(displayRecords), [displayRecords]);
  const [selectedCellKey, setSelectedCellKey] = useState("");
	  const [selectedStudentId, setSelectedStudentId] = useState("");
	  const [selectedColumnId, setSelectedColumnId] = useState("");
  const [openStatusPickerKey, setOpenStatusPickerKey] = useState("");
	  const statusPickerSuppressedUntilRef = useRef(0);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const noteEditorRef = useRef<HTMLTextAreaElement | null>(null);
  const [isActionBusy, setIsActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [confirmSubmitNext, setConfirmSubmitNext] = useState(false);
  const selectedCell = selectedCellKey
    ? displayRecords.cells.find((cell) => makeCellDomKey(cell) === selectedCellKey) ?? null
    : null;
  const selectedStudentLabel = selectedStudentId
    ? getStudentName(displayConfig, selectedStudentId)
    : "";
  const selectedColumnCandidate = selectedColumnId
    ? columns.find((column) => column.id === selectedColumnId) ?? null
    : null;
  const selectedColumnHiddenByWeekTab =
    Boolean(selectedColumnCandidate?.schoolDate) &&
    state.dayMode === "week" &&
    isNarrowRecordsViewport &&
    selectedColumnCandidate?.schoolDate !== state.schoolDate;
  const selectedColumn = selectedColumnHiddenByWeekTab ? null : selectedColumnCandidate;
  const effectiveSelectedColumnId = selectedColumn?.id ?? "";
  const selectedColumnLabel = selectedColumn?.summaryLabel ?? "";
  const visibleActionFilter: AttendanceRecordsFilter = useMemo(
    () => (
      state.dayMode === "week" && isNarrowRecordsViewport
        ? { ...filter, dayMode: "day" as const, schoolDate: state.schoolDate }
        : filter
    ),
    [filter, isNarrowRecordsViewport, state.dayMode, state.schoolDate],
  );

  const selectCell = (cell: AttendanceCellRecord, columnId?: string) => {
    const nextColumnId = columnId ?? columns.find((column) => column.matcher(cell))?.id ?? "";
    setSelectedCellKey(makeCellDomKey(cell));
    setSelectedStudentId(cell.studentId);
    setSelectedColumnId(nextColumnId);
    setNoteDraft(cell.note ?? "");
  };

  const selectStudentRow = (row: StudentRecordRow) => {
    setSelectedStudentId(row.studentId);
    const activeColumn = selectedColumn;
    const cell = activeColumn ? row.cells.find(activeColumn.matcher) ?? null : null;
    if (cell && activeColumn) {
      selectCell(cell, activeColumn.id);
      return;
    }
    setSelectedCellKey("");
    setNoteDraft("");
    setNoteEditorOpen(false);
  };

  const selectColumn = (column: GridColumn) => {
    setSelectedColumnId(column.id);
    const row = selectedStudentId
      ? rows.find((entry) => entry.studentId === selectedStudentId) ?? null
      : null;
    const cell = row?.cells.find(column.matcher) ?? null;
    if (cell) {
      selectCell(cell, column.id);
      return;
    }
    setSelectedCellKey("");
    setNoteDraft("");
    setNoteEditorOpen(false);
  };

  useEffect(() => {
    if (!selectedCellKey) {
      setNoteDraft("");
      setNoteEditorOpen(false);
      return;
    }
    if (!selectedCell) {
      setSelectedCellKey("");
      setNoteDraft("");
      setNoteEditorOpen(false);
      return;
    }
    setNoteDraft(selectedCell.note ?? "");
  }, [selectedCellKey, selectedCell?.version, selectedCell?.note, selectedCell?.studentId]);

  useEffect(() => {
    if (selectedStudentId && !rows.some((row) => row.studentId === selectedStudentId)) {
      setSelectedStudentId("");
    }
  }, [rows, selectedStudentId]);

  useEffect(() => {
    if (
      selectedColumnId &&
      !columns.some((column) => column.id === selectedColumnId)
    ) {
      setSelectedColumnId("");
      setSelectedCellKey("");
      setNoteDraft("");
      setNoteEditorOpen(false);
    }
  }, [columns, selectedColumnId]);

  useEffect(() => {
    if (!selectedColumnHiddenByWeekTab) return;
    setSelectedColumnId("");
    setSelectedCellKey("");
    setNoteDraft("");
    setNoteEditorOpen(false);
  }, [selectedColumnHiddenByWeekTab]);

  useEffect(() => {
    if (!noteEditorOpen) return;
    requestAnimationFrame(() => {
      noteEditorRef.current?.focus();
    });
  }, [noteEditorOpen, selectedCellKey]);

  useEffect(() => {
    if (!Object.keys(optimisticCells).length) return;
    setOptimisticCells((current) => {
      let changed = false;
      const next = { ...current };
      for (const cell of baseDisplayRecords.cells) {
        const key = makeCellDomKey(cell);
        const optimisticCell = current[key];
        if (!optimisticCell) continue;
        if (
          optimisticCell.status === cell.status &&
          (optimisticCell.note ?? null) === (cell.note ?? null)
        ) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [baseDisplayRecords, optimisticCells]);

  useEffect(() => {
    if (context.isLoading) return;
    const nextSearch = `?${filterToSearch(filter)}`;
    if (location.search !== nextSearch) {
      navigate({ search: nextSearch }, { replace: true });
    }
  }, [context.isLoading, filter.schoolDate, filter.dayMode, filter.divisionId, filter.gradeId, filter.classroomId, filter.periodId, location.search, navigate]);

  const saveCellStatus = async (
    cell: AttendanceCellRecord,
    status: AttendanceStatus,
    note = cell.note,
  ) => {
    const result = await bundle.transport.mutate("saveAttendanceRecordCell", {
      input: {
        ...bundle.runtimeScope,
        studentId: cell.studentId,
        schoolDate: cell.schoolDate,
        classroomId: cell.classroomId,
        periodId: cell.periodId,
        status,
        note,
        expectedVersion: cell.version,
      },
    });
    return result.cell;
  };

  const updateCell = async (
    cell: AttendanceCellRecord,
    status: AttendanceStatus,
    note = cell.note,
  ) => {
    const key = makeCellDomKey(cell);
    const persistedCell =
      baseDisplayRecords.cells.find((baseCell) => makeCellDomKey(baseCell) === key) ??
      cell;
    const previousSave =
      cellSaveQueuesRef.current[key] ?? Promise.resolve(persistedCell);
    setActionMessage("");
    setOptimisticCells((current) => ({
      ...current,
      [key]: {
        ...(current[key] ?? cell),
        status,
        note,
        version: (current[key]?.version ?? cell.version) + 1,
        updatedAt: Math.floor(Date.now() / 1000),
        updatedBy: "attendance-ui",
        sourceSurface: "records",
      },
    }));
    const savePromise = previousSave
      .catch(() => persistedCell)
      .then((latestCell) => saveCellStatus(latestCell, status, note));
    cellSaveQueuesRef.current[key] = savePromise;
    try {
      const savedCell = await savePromise;
      setOptimisticCells((current) => ({
        ...current,
        [key]: current[key] && current[key].version > savedCell.version
          ? current[key]
          : savedCell,
      }));
      records.reload();
    } catch (reason) {
      setOptimisticCells((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      setActionMessage(
        reason instanceof Error
          ? reason.message
          : "Attendance status update failed. Please try again.",
      );
    } finally {
      if (cellSaveQueuesRef.current[key] === savePromise) {
        delete cellSaveQueuesRef.current[key];
      }
    }
  };

  const waitForPendingCellSaves = async () => {
    const pendingSaves = Object.values(cellSaveQueuesRef.current);
    if (!pendingSaves.length) return;
    await Promise.allSettled(pendingSaves);
  };

  const runRecordsAction = async (
    successMessage: string,
    action: () => Promise<number | void>,
  ) => {
    setIsActionBusy(true);
    setActionMessage("");
    try {
      await waitForPendingCellSaves();
      const changedCount = await action();
      setActionMessage(
        typeof changedCount === "number"
          ? `${successMessage} (${changedCount} visible cell${changedCount === 1 ? "" : "s"}).`
          : `${successMessage}.`,
      );
      records.reload();
    } catch (reason) {
      setActionMessage(
        reason instanceof Error
          ? reason.message
          : "Attendance action failed. Please try again.",
      );
    } finally {
      setIsActionBusy(false);
    }
  };

  const runBulkAction = async (
    action:
      | "mark-row-present"
      | "mark-column-present"
      | "mark-visible-present"
      | "reset-row"
      | "reset-column"
      | "reset-visible-view",
    scope: {
      studentId?: string | null;
      columnSchoolDate?: string | null;
      columnPeriodId?: string | null;
    } = {},
  ) => {
    const result = await bundle.transport.mutate("applyAttendanceBulkAction", {
      input: {
        ...visibleActionFilter,
        ...scope,
        action,
      },
    });
    return result.changedCellCount;
  };

  const resetVisible = async () => {
    const result = await bundle.transport.mutate("resetAttendanceRecordScope", {
      input: visibleActionFilter,
    });
    return result.changedCellCount;
  };

  const markVisiblePresent = async () => {
    return runBulkAction("mark-visible-present");
  };

  const submitScope = async () => {
    if (!filter.classroomId) return 0;
    const result = await bundle.transport.mutate("submitAttendanceRecordScope", {
      input: {
        ...bundle.runtimeScope,
        schoolDate: filter.schoolDate,
        classroomId: filter.classroomId,
        periodId: filter.periodId ?? null,
        dayMode: filter.dayMode,
        acknowledgeDefaultPresent: true,
      },
    });
    return result.submittedCellCount;
  };

  const markRowPresent = (studentId: string) => {
    void runRecordsAction("Selected student marked Present", () =>
      runBulkAction("mark-row-present", { studentId }),
    );
  };

  const markColumnPresent = (columnId: string) => {
    const column = columns.find((entry) => entry.id === columnId);
    void runRecordsAction("Selected period marked Present", () =>
      column
        ? runBulkAction("mark-column-present", column.actionScope)
        : Promise.reject(new Error("Attendance column action requires a visible column scope.")),
    );
  };

  const resetRow = (studentId: string) => {
    void runRecordsAction("Selected student reset to default status with notes retained", () =>
      runBulkAction("reset-row", { studentId }),
    );
  };

  const resetColumn = (columnId: string) => {
    const column = columns.find((entry) => entry.id === columnId);
    void runRecordsAction("Selected period reset to default status with notes retained", () =>
      column
        ? runBulkAction("reset-column", column.actionScope)
        : Promise.reject(new Error("Attendance column action requires a visible column scope.")),
    );
  };

  const exportRecords = async () => {
    const result = await bundle.transport.mutate("exportAttendanceRecordsCsv", {
      input: {
        scope: "records-current-filter",
        recordsFilter: filter,
        includeNotes: true,
      },
    });
    deliverExportResult(result);
  };

  const exportRecordsPdf = async () => {
    const result = await bundle.transport.mutate("exportAttendanceContext", {
      input: {
        context: "new-records",
        format: "pdf",
        recordsFilter: filter,
        includeNotes: true,
      },
    });
    deliverExportResult(result);
  };

  const handleResetVisible = () => {
    void runRecordsAction(
      "Visible records reset to default status with notes retained",
      resetVisible,
    );
  };

  const handleMarkVisiblePresent = () => {
    void runRecordsAction("Visible records marked Present", markVisiblePresent);
  };

  const handleSubmitScope = () => {
    void runRecordsAction("Batch submitted for the selected scope", submitScope);
  };

  const handleSubmitNext = () => {
    setConfirmSubmitNext(true);
  };

  const confirmSubmitAndNext = () => {
    setConfirmSubmitNext(false);
    void runRecordsAction("Batch submitted and advanced to the next classroom", async () => {
      const changedCount = await submitScope();
      const remainingClassroomIds = unrecordedClassroomIds.filter(
        (classroomId) => classroomId !== state.classroomId,
      );
      const currentIndex = unrecordedClassroomIds.indexOf(state.classroomId);
      const nextClassroomId =
        remainingClassroomIds.find((classroomId) => (
          currentIndex < 0 || unrecordedClassroomIds.indexOf(classroomId) > currentIndex
        )) ??
        remainingClassroomIds[0] ??
        null;
      if (nextClassroomId) {
        setState((current) => stateForClassroom(displayConfig, nextClassroomId, current));
      }
      return changedCount;
    });
  };

  return (
    <AttendanceShell
      bundle={bundle}
      title="Records"
      subtitle={`${formatDate(filter.schoolDate)} / ${state.dayMode === "week" ? "Week view" : "Day view"}`}
      testId="attendance-new-records-root"
      showPageHeader={false}
      actions={
        <>
          <ExportDropdown onExport={exportRecords} onExportPdf={exportRecordsPdf} />
          <RecordsSubmissionActions
            isBusy={isActionBusy}
            submitDisabled={!filter.classroomId || records.isLoading || !!records.error || !!context.error || !displayRecords.cells.length}
            onSubmit={handleSubmitScope}
            onSubmitNext={handleSubmitNext}
          />
        </>
      }
    >
      <div data-testid="attendance-records-root" className="block min-w-0 w-full max-w-full">
      <FilterControls
        config={displayConfig}
        state={state}
        setState={setState}
        classroomPagerIds={unrecordedClassroomIds}
        compactLayout
        compactPager="next"
        maxDate={context.data.currentSchoolDate || schoolTodayIso()}
      />
      {actionMessage ? (
        <p className="mb-4 rounded-[8px] border border-[var(--border)] bg-white px-4 py-3 text-sm text-[var(--text-secondary)]" role="status">
          {actionMessage}
        </p>
      ) : null}
      {confirmSubmitNext ? (
        <div
          role="dialog"
          aria-label="Submit current entries and continue"
          className="rounded-[8px] border border-[var(--primary)] bg-white p-4 shadow-[0_12px_28px_rgba(36,50,53,0.12)]"
        >
          <p className="text-sm font-semibold text-[var(--iron-grey)]">
            Submit current entries and continue to the next classroom?
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={confirmSubmitAndNext}
              className="h-9 rounded-[8px] bg-[var(--primary)] px-3 text-sm font-semibold text-white"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setConfirmSubmitNext(false)}
              className="h-9 rounded-[8px] border border-[var(--border)] bg-white px-3 text-sm font-semibold text-[var(--iron-grey)]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {records.error || context.error ? (
        <ErrorState message={errorMessageFor(records.error ?? context.error, "Unable to load attendance records.")} />
      ) : records.isLoading || context.isLoading ? (
        <EmptyState message="Attendance records are syncing for the selected filter." />
      ) : !displayRecords.cells.length ? (
        <EmptyState message="No records match the selected filter." />
      ) : (
        <div className="space-y-4">
          {displayRecords.overwriteWarning ? (
            <div className="rounded-[8px] border border-[var(--coral)] bg-[rgba(255, 95, 95,0.14)] px-4 py-3 text-sm text-[var(--iron-grey)]">
              {displayRecords.overwriteWarning}
            </div>
          ) : null}
          <RecordsActionsMenu
            rows={rows}
            columns={visibleColumns}
            selectedStudentId={selectedStudentId}
            selectedColumnId={effectiveSelectedColumnId}
            selectedStudentLabel={selectedStudentLabel}
            selectedColumnLabel={selectedColumnLabel}
            isBusy={isActionBusy}
            onMarkRowPresent={markRowPresent}
            onResetRow={resetRow}
            onMarkColumnPresent={markColumnPresent}
            onResetColumn={resetColumn}
            onMarkVisiblePresent={handleMarkVisiblePresent}
            onResetVisible={handleResetVisible}
          />
          <div className="min-w-0 overflow-hidden rounded-[10px] border border-[var(--border)] bg-white shadow-[0_0_3px_2px_rgba(206,242,237,0.39)]">
            <div
              data-testid="attendance-records-grid-scroll"
              className="w-full max-w-full overflow-auto"
            >
            <table aria-label="Records attendance grid" className="w-full min-w-fit border-collapse text-left text-sm">
              <thead>
                {weekDayGroups.length ? (
                  <tr className="border-b border-[var(--border)] bg-[#F4F6F3]">
                    <th
                      scope="col"
                      role="columnheader"
                      className="sticky left-0 z-20 w-[200px] min-w-[200px] bg-[#F4F6F3] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]"
                      style={{ width: 200, minWidth: 200 }}
                    >
                      Student
                    </th>
                    {weekDayGroups.map((schoolDate) => (
                      <th
                        key={schoolDate}
                        scope="col"
                        role="columnheader"
                        colSpan={columns.filter((column) => column.schoolDate === schoolDate).length}
                        className={`bg-[#F4F6F3] px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] ${
                          schoolDate === state.schoolDate ? "" : "hidden md:table-cell"
                        }`}
                      >
                        {formatDate(schoolDate)}
                      </th>
                    ))}
                    <th
                      scope="col"
                      role="columnheader"
                      className="min-w-[200px] bg-[#F4F6F3] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]"
                      style={{ minWidth: 200 }}
                    >
                      Notes
                    </th>
                  </tr>
                ) : null}
                <tr className="border-b border-[var(--border)] bg-[#F4F6F3]">
                  <th
                    scope="col"
                    role="columnheader"
                    className="sticky left-0 z-20 w-[200px] min-w-[200px] bg-[#F4F6F3] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]"
                    style={{ width: 200, minWidth: 200 }}
                  >
                    Student
                  </th>
                  {columns.map((column) => (
	                    <th
	                      key={column.id}
	                      scope="col"
	                      role="columnheader"
	                      aria-label={column.label}
	                      aria-selected={selectedColumnId === column.id}
	                      data-testid="attendance-records-period-header"
	                      data-column-id={column.id}
	                      data-selected={selectedColumnId === column.id ? "true" : "false"}
	                      className={`w-16 min-w-16 border-l border-[var(--border)] px-1 py-2 text-center font-semibold text-[var(--text-secondary)] ${
	                        selectedColumnId === column.id ? "bg-[rgba(38,151,162,0.14)] shadow-[inset_0_-2px_0_var(--primary)]" : "bg-[#F4F6F3]"
	                      } ${
                        column.schoolDate && column.schoolDate !== state.schoolDate
                          ? "hidden md:table-cell"
                          : ""
                      }`}
	                      style={{
	                        width: 64,
	                        minWidth: 64,
	                        backgroundColor: selectedColumnId === column.id
	                          ? "rgba(38, 151, 162, 0.14)"
	                          : "#F4F6F3",
	                      }}
		                    >
                      <button
                        type="button"
                        aria-label={column.ariaLabel}
                        aria-pressed={selectedColumnId === column.id}
                        onClick={() => selectColumn(column)}
                        className={`flex min-h-[36px] w-full flex-col items-center justify-center rounded-[6px] border px-1 text-center transition-colors hover:bg-[rgba(38,151,162,0.10)] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.45)] ${
                          selectedColumnId === column.id
                            ? "border-transparent bg-transparent text-[var(--primary)]"
                            : "border-transparent"
                        }`}
                      >
                        <span>{column.label}</span>
                        {column.subLabel ? (
                          <span className="block text-xs font-medium text-[var(--text-secondary)]">
                            {column.subLabel}
                          </span>
                        ) : null}
                      </button>
                    </th>
                  ))}
                  <th
                    scope="col"
                    role="columnheader"
                    className="min-w-[200px] border-l border-[var(--border)] bg-[#F4F6F3] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]"
                    style={{ minWidth: 200 }}
                  >
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const link = displayRecords.studentLinks.find(
                    (entry) => entry.studentId === row.studentId,
                  );
                  const grade = getGradeForClassroom(
                    displayConfig,
                    row.cells[0]?.classroomId ?? "",
                  );
                  const firstCell = row.cells[0] ?? null;
                  const rowSelected = selectedStudentId === row.studentId;
                  return (
	                    <tr
	                      key={row.studentId}
	                      data-testid="attendance-records-student-row"
	                      data-student-id={row.studentId}
	                      data-selected={rowSelected ? "true" : "false"}
		                      className={`border-b border-[var(--border)] last:border-b-0 ${
		                        rowSelected ? "bg-[rgba(38,151,162,0.08)]" : "hover:bg-[rgba(38,151,162,0.05)]"
		                      }`}
		                      style={{
		                        backgroundColor: rowSelected ? "rgba(38, 151, 162, 0.08)" : undefined,
		                      }}
	                    >
                      <th
                        scope="row"
                        className={`sticky left-0 z-10 w-[200px] min-w-[200px] px-4 py-3 text-left align-middle ${
                          rowSelected ? "bg-[rgba(38,151,162,0.14)]" : "bg-white"
                        }`}
	                        style={{
	                          width: 200,
	                          minWidth: 200,
	                          backgroundColor: rowSelected ? "rgba(38, 151, 162, 0.14)" : "#FFFFFF",
	                        }}
                      >
                        <button
                          type="button"
                          aria-label={`Select student row ${getStudentName(displayConfig, row.studentId)}`}
                          aria-pressed={rowSelected}
                          onClick={() => selectStudentRow(row)}
                          className={`flex w-full items-center gap-3 rounded-[6px] border text-left transition-colors hover:bg-[rgba(38,151,162,0.08)] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.45)] ${
                            rowSelected ? "border-transparent bg-transparent" : "border-transparent"
                          }`}
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--muted)] text-xs font-semibold uppercase text-[var(--text-secondary)]">
                            {getStudentName(displayConfig, row.studentId)
                              .split(/\s+/)
                              .map((part) => part[0])
                              .join("")
                              .slice(0, 2)}
                          </span>
                          <span>
                            <span className="block font-semibold text-[var(--iron-grey)]">
                              {getStudentName(displayConfig, row.studentId)}
                            </span>
                            <span className="block text-xs text-[var(--text-secondary)]">
                              {grade?.label ?? "Grade"}
                            </span>
                          </span>
                        </button>
                        {link ? (
                          <Link
                            to={link.route}
                            className="sr-only"
                          >
                            View History
                          </Link>
                        ) : null}
                      </th>
                      {columns.map((column) => {
                        const cell = row.cells.find(column.matcher);
                        const columnSelected = selectedColumnId === column.id;
                        return (
	                          <td
	                            key={column.id}
	                            data-testid="attendance-records-cell-slot"
	                            data-column-id={column.id}
	                            data-row-selected={rowSelected ? "true" : "false"}
	                            data-column-selected={columnSelected ? "true" : "false"}
	                            className={`w-16 min-w-16 border-l border-[var(--border)] px-1.5 py-2 align-middle ${
	                              rowSelected || columnSelected ? "bg-[rgba(38,151,162,0.07)]" : ""
	                            } ${
                              column.schoolDate && column.schoolDate !== state.schoolDate
                                ? "hidden md:table-cell"
                                : ""
                            }`}
	                            style={{
	                              width: 64,
	                              minWidth: 64,
	                              backgroundColor: rowSelected || columnSelected
	                                ? "rgba(38, 151, 162, 0.07)"
	                                : undefined,
	                            }}
                          >
                            {cell ? (
                              <StatusCell
                                cell={cell}
                                studentName={getStudentName(displayConfig, row.studentId)}
                                periodLabel={column.summaryLabel}
                                selected={makeCellDomKey(cell) === selectedCellKey}
                                pickerOpen={makeCellDomKey(cell) === openStatusPickerKey}
                                hasOpenPicker={Boolean(openStatusPickerKey)}
                                onSelect={() => {
                                  selectCell(cell, column.id);
                                }}
	                                onPickerOpen={() => {
	                                  if (Date.now() < statusPickerSuppressedUntilRef.current) {
	                                    return;
	                                  }
	                                  setOpenStatusPickerKey(makeCellDomKey(cell));
	                                }}
	                                onPickerClose={() => {
	                                  const cellKey = makeCellDomKey(cell);
	                                  setOpenStatusPickerKey((current) => (
	                                    current === cellKey ? "" : current
	                                  ));
	                                }}
		                                onPickerDismiss={() => {
		                                  statusPickerSuppressedUntilRef.current = Date.now() + STATUS_PICKER_CLOSE_DELAY_MS;
		                                  setOpenStatusPickerKey("");
		                                }}
	                                onStatus={(status) => void updateCell(cell, status)}
	                              />
                            ) : (
                              <span className="text-xs text-[var(--text-secondary)]">
                                No record
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td
                        className={`min-w-[200px] border-l border-[var(--border)] px-4 py-3 align-middle ${
                          rowSelected ? "bg-[rgba(38,151,162,0.05)]" : ""
                        }`}
                        style={{ minWidth: 200 }}
                      >
                        <button
                          type="button"
                          data-testid="attendance-notes-column-trigger"
                          className="w-full truncate text-left text-sm text-[var(--text-secondary)] hover:text-[var(--iron-grey)]"
                          onClick={() => {
                            if (!firstCell) return;
                            selectCell(firstCell);
                            setNoteEditorOpen(true);
                          }}
                        >
                          {firstCell?.note ? firstCell.note : "Add a note..."}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <StatusLegend />
          </div>
          {noteEditorOpen && selectedCell ? (
          <section className="rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] p-4 shadow-[0_10px_24px_rgba(36,50,53,0.08)]">
            <label className="text-sm font-semibold text-[var(--iron-grey)]">
              Notes
              <textarea
                ref={noteEditorRef}
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                onBlur={() => {
                  if (selectedCell) {
                    void updateCell(selectedCell, selectedCell.status, noteDraft);
                  }
                }}
                className="mt-2 min-h-[96px] w-full rounded-[8px] border border-[var(--border)] bg-white px-3 py-2 text-sm text-[var(--iron-grey)]"
                placeholder="Select a record to review or edit notes."
              />
            </label>
          </section>
          ) : null}
        </div>
      )}
      </div>
    </AttendanceShell>
  );
};

const makeCellDomKey = (cell: AttendanceCellRecord) =>
  `${cell.schoolDate}:${cell.classroomId}:${cell.periodId}:${cell.studentId}`;

const StatusLegend = () => (
  <div
    data-testid="attendance-records-ledger"
    aria-label="Attendance records legend"
    className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--border)] bg-[rgba(244,246,243,0.3)] px-4 py-3 text-xs text-[var(--text-secondary)]"
  >
    {statusOrder.map((status) => (
      <span key={status}>
        {STATUS_UI_LABELS[status].shortLabel} = {STATUS_UI_LABELS[status].label}
      </span>
    ))}
    <span className="basis-full text-left text-[var(--text-secondary)] sm:ml-auto sm:basis-auto sm:text-right">
      Tip: focus a status button, press Space to cycle or P / A / L / E to set. Tab moves between controls.
    </span>
  </div>
);

const StatusCell = ({
  cell,
  studentName,
  periodLabel,
  selected,
  pickerOpen,
  hasOpenPicker,
  onSelect,
  onPickerOpen,
  onPickerClose,
  onPickerDismiss,
  onStatus,
}: {
  cell: AttendanceCellRecord;
  studentName: string;
  periodLabel: string;
  selected: boolean;
  pickerOpen: boolean;
  hasOpenPicker: boolean;
  onSelect: () => void;
  onPickerOpen: () => void;
  onPickerClose: () => void;
  onPickerDismiss: () => void;
  onStatus: (status: AttendanceStatus) => void;
}) => {
	  const groupRef = useRef<HTMLDivElement | null>(null);
	  const buttonRef = useRef<HTMLButtonElement | null>(null);
	  const closePickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	  const openPickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	  const suppressPickerUntilRef = useRef(0);
	  const restoreButtonFocusRef = useRef(false);
  const [hoveredPickerStatus, setHoveredPickerStatus] = useState<AttendanceStatus | null>(null);
  const [pickerPosition, setPickerPosition] = useState<{
    left: number;
    top: number;
    placement: "top" | "bottom";
  } | null>(null);
  const nightModePortal = useYstnjNightModeAttribute();
  const activeLabel = STATUS_UI_LABELS[cell.status];
  const activeCellStyle = statusCellStyle(cell.status);

  const updateStatusPickerPosition = () => {
    if (typeof window === "undefined") return;
    const anchor = groupRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const left = Math.min(
      Math.max(STATUS_PICKER_GAP, rect.left + rect.width / 2 - STATUS_PICKER_WIDTH / 2),
      Math.max(STATUS_PICKER_GAP, window.innerWidth - STATUS_PICKER_WIDTH - STATUS_PICKER_GAP),
    );
    const belowSpace = window.innerHeight - rect.bottom;
    const aboveSpace = rect.top;
    const placement =
      belowSpace < STATUS_PICKER_ESTIMATED_HEIGHT + STATUS_PICKER_GAP && aboveSpace > belowSpace
        ? "top"
        : "bottom";
    const preferredTop =
      placement === "top"
        ? rect.top - STATUS_PICKER_ESTIMATED_HEIGHT - STATUS_PICKER_GAP
        : rect.bottom + STATUS_PICKER_GAP;
    const top = Math.min(
      Math.max(STATUS_PICKER_GAP, preferredTop),
      Math.max(STATUS_PICKER_GAP, window.innerHeight - STATUS_PICKER_ESTIMATED_HEIGHT - STATUS_PICKER_GAP),
    );
    setPickerPosition({ left, top, placement });
  };

  const cancelOpenStatusPicker = () => {
    if (!openPickerTimerRef.current) return;
    clearTimeout(openPickerTimerRef.current);
    openPickerTimerRef.current = null;
  };
  const cancelCloseStatusPicker = () => {
    if (!closePickerTimerRef.current) return;
    clearTimeout(closePickerTimerRef.current);
    closePickerTimerRef.current = null;
  };
  const closeStatusPickerSoon = () => {
    cancelCloseStatusPicker();
    closePickerTimerRef.current = setTimeout(() => {
      setHoveredPickerStatus(null);
      onPickerClose();
      closePickerTimerRef.current = null;
    }, STATUS_PICKER_CLOSE_DELAY_MS);
  };
  const chooseStatus = (status: AttendanceStatus) => {
    restoreButtonFocusRef.current = document.activeElement === buttonRef.current;
    onSelect();
    onStatus(status);
    suppressPickerUntilRef.current = Date.now() + STATUS_PICKER_CLOSE_DELAY_MS;
    cancelOpenStatusPicker();
    cancelCloseStatusPicker();
    setHoveredPickerStatus(null);
    onPickerDismiss();
  };
  const openStatusPicker = () => {
    if (Date.now() < suppressPickerUntilRef.current) {
      return;
    }
    cancelCloseStatusPicker();
    cancelOpenStatusPicker();
    setHoveredPickerStatus(null);
    updateStatusPickerPosition();
    if (pickerOpen || !hasOpenPicker) {
      onPickerOpen();
      return;
    }
    openPickerTimerRef.current = setTimeout(() => {
      onPickerOpen();
      openPickerTimerRef.current = null;
    }, STATUS_PICKER_SWITCH_DELAY_MS);
  };
  const cycleStatus = () => {
    restoreButtonFocusRef.current = document.activeElement === buttonRef.current;
    onSelect();
    onStatus(nextAttendanceStatus(cell.status));
    suppressPickerUntilRef.current = Date.now() + STATUS_PICKER_CLOSE_DELAY_MS;
    cancelOpenStatusPicker();
    cancelCloseStatusPicker();
    setHoveredPickerStatus(null);
    onPickerDismiss();
  };

  useEffect(
    () => () => {
      cancelOpenStatusPicker();
      cancelCloseStatusPicker();
    },
    [],
  );

  useEffect(() => {
    if (!pickerOpen) return undefined;
    updateStatusPickerPosition();
    window.addEventListener("resize", updateStatusPickerPosition);
    window.addEventListener("scroll", updateStatusPickerPosition, true);
    return () => {
      window.removeEventListener("resize", updateStatusPickerPosition);
      window.removeEventListener("scroll", updateStatusPickerPosition, true);
    };
  }, [pickerOpen]);

  useEffect(() => {
    if (!restoreButtonFocusRef.current) return;
    requestAnimationFrame(() => {
      buttonRef.current?.focus();
      restoreButtonFocusRef.current = false;
    });
  }, [cell.status, cell.version]);

  const pickerMenu = pickerOpen && pickerPosition ? (
    <div
      role="menu"
      aria-label="Attendance status"
      data-placement={pickerPosition.placement}
      data-ystnj-night-mode={nightModePortal ? "true" : "false"}
      className="fixed z-[1000] flex min-w-[140px] flex-col rounded-[12px] border p-1"
      onMouseEnter={cancelCloseStatusPicker}
      onMouseLeave={() => {
        setHoveredPickerStatus(null);
        closeStatusPickerSoon();
      }}
      style={{
        backgroundColor: nightModePortal ? "#050F0D" : "#FFFFFF",
        borderColor: nightModePortal ? "rgba(38,151,162,0.42)" : "#E5E7EB",
        color: nightModePortal ? "#E6F3F0" : "#404F4D",
        left: pickerPosition.left,
        top: pickerPosition.top,
        width: STATUS_PICKER_WIDTH,
        boxShadow: nightModePortal
          ? "0 12px 26px rgba(0,0,0,0.48), 0 0 0 1px rgba(38,151,162,0.18)"
          : "0 4px 6px 2px rgba(206,242,237,0.39)",
      }}
    >
      {statusOrder.map((status) => (
        <button
          key={status}
          type="button"
          role="menuitem"
          onMouseDown={(event) => event.preventDefault()}
          onMouseEnter={() => setHoveredPickerStatus(status)}
          onMouseLeave={() => setHoveredPickerStatus((current) => (
            current === status ? null : current
          ))}
          onClick={() => chooseStatus(status)}
          className="text-sm font-semibold transition-colors hover:brightness-[0.98] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/35"
          style={statusPickerOptionStyle(status, status === cell.status, hoveredPickerStatus === status, nightModePortal)}
        >
          <span
            className="flex items-center justify-center rounded-[6px] text-sm"
            style={statusPickerBadgeStyle(status, status === cell.status, nightModePortal)}
          >
            {STATUS_UI_LABELS[status].shortLabel}
          </span>
          <span>{STATUS_UI_LABELS[status].label}</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div
      ref={groupRef}
      data-testid="attendance-status-cell"
      className={`group relative flex w-full cursor-pointer justify-center rounded-[8px] ${
        selected ? "z-20 bg-[rgba(38,151,162,0.12)] ring-2 ring-[rgba(38,151,162,0.35)]" : ""
      }`}
      role="group"
      aria-label={`${studentName} ${compactDate(cell.schoolDate)} ${periodLabel} attendance cell`}
      onMouseEnter={openStatusPicker}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          cycleStatus();
        }
      }}
      onMouseLeave={(event) => {
        suppressPickerUntilRef.current = 0;
        cancelOpenStatusPicker();
        const relatedTarget = event.relatedTarget;
        if (!(relatedTarget instanceof Node) || !event.currentTarget.contains(relatedTarget)) {
          closeStatusPickerSoon();
        }
      }}
      onBlur={(event) => {
        const relatedTarget = event.relatedTarget;
        if (!(relatedTarget instanceof Node) || !event.currentTarget.contains(relatedTarget)) {
          onPickerClose();
        }
      }}
    >
      <button
        type="button"
        ref={buttonRef}
        onClick={cycleStatus}
        onFocus={onSelect}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setHoveredPickerStatus(null);
            onPickerDismiss();
            return;
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            openStatusPicker();
            return;
          }
          if (event.key === " " || event.key === "Space" || event.key === "Spacebar") {
            event.preventDefault();
            cycleStatus();
            return;
          }
          const nextStatus = statusShortcutByKey.get(event.key.toLowerCase());
          if (!nextStatus) return;
          event.preventDefault();
          chooseStatus(nextStatus);
        }}
        className={`relative flex h-10 w-full items-center justify-center rounded-[6px] border text-[13px] font-semibold leading-[19.5px] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50 ${statusCellClassName(cell.status)}`}
        aria-label={`${studentName} ${compactDate(cell.schoolDate)} ${periodLabel} status ${STATUS_UI_LABELS[cell.status].label}`}
        aria-keyshortcuts="Space P A L E"
        aria-haspopup="menu"
        aria-expanded={pickerOpen}
        style={{
          ...activeCellStyle,
          borderStyle: "solid",
          borderWidth: 1,
          minHeight: 40,
          height: 40,
        }}
      >
        {cell.version === 0 ? (
          <span
            aria-label="Pre-filled record"
            className="absolute left-1.5 top-1.5 h-2 w-2 rounded-full bg-[var(--text-secondary)]/70"
          />
        ) : null}
        <span style={{ color: activeCellStyle.color }}>
          {activeLabel.shortLabel}
        </span>
      </button>
      {pickerMenu && typeof document !== "undefined" ? createPortal(pickerMenu, document.body) : null}
    </div>
  );
};

const TeacherViewPage = ({ bundle }: { bundle: AttendanceFeatureBundle }) => {
  const context = useAttendanceContext(bundle);
  const [state, setState] = useState<RecordsFilterState>({
    schoolDate: DEFAULT_CONTEXT.currentSchoolDate,
    dayMode: "day",
    divisionId: "",
    gradeId: "",
    classroomId: "",
    periodId: "",
  });
  const [message, setMessage] = useState("");
  const [showPreviewBanner, setShowPreviewBanner] = useState(true);
  const [openNoteKeys, setOpenNoteKeys] = useState<Record<string, true>>({});
  const [previewOverrides, setPreviewOverrides] = useState<
    Record<string, { status: AttendanceStatus; note: string | null; version: number }>
  >({});
  const teacherMaxDate = context.data.currentSchoolDate || schoolTodayIso();

  useEffect(() => {
    const config = context.data.config;
    if (
      !config.divisions.length ||
      !config.grades.length ||
      !config.classrooms.length ||
      !config.periods.length
    ) {
      return;
    }
    const firstPeriodId =
      [...config.periods].sort((left, right) => left.sortOrder - right.sortOrder)[0]?.periodId ?? "";
    const contextSchoolDate = (
      context.data.currentSchoolDate && context.data.currentSchoolDate <= teacherMaxDate
        ? context.data.currentSchoolDate
        : teacherMaxDate
    ) || DEFAULT_CONTEXT.currentSchoolDate;
    setState((current) => {
      const requestedSchoolDate =
        !current.schoolDate || current.schoolDate === DEFAULT_CONTEXT.currentSchoolDate
          ? contextSchoolDate
          : current.schoolDate;
      const schoolDate =
        requestedSchoolDate && requestedSchoolDate <= teacherMaxDate
          ? requestedSchoolDate
          : teacherMaxDate;
      const normalized = normalizeRecordsFilterState(
        config,
        { ...current, schoolDate, dayMode: "day" },
        {
          schoolDate,
          dayMode: "day",
          divisionId: current.divisionId || undefined,
          gradeId: current.gradeId || undefined,
          classroomId: current.classroomId || undefined,
          periodId: current.periodId || undefined,
        },
        schoolDate,
      );
      const next = {
        ...normalized,
        dayMode: "day" as const,
        periodId: normalized.classroomId
          ? normalized.periodId || firstPeriodId
          : "",
      };
      return sameRecordsFilterState(current, next) ? current : next;
    });
  }, [context.data.config, context.data.currentSchoolDate, teacherMaxDate]);

  const filter = useMemo(
    () => createFilter(bundle, state),
    [
      bundle.runtimeScope.organizationId,
      bundle.runtimeScope.schoolId,
      state.schoolDate,
      state.dayMode,
      state.divisionId,
      state.gradeId,
      state.classroomId,
      state.periodId,
    ],
  );
  const records = useAsyncQuery<AttendanceRecordsGrid>(
    () => bundle.transport.query("listAttendanceRecords", { input: filter }),
    [
      bundle.transport,
      filter.organizationId,
      filter.schoolId,
      filter.schoolDate,
      filter.dayMode,
      filter.divisionId,
      filter.gradeId,
      filter.classroomId,
      filter.periodId,
    ],
    { filter, cells: [], studentLinks: [], overwriteWarning: null },
  );
  const filtersComplete = Boolean(state.divisionId && state.gradeId && state.classroomId && state.periodId);
  const sourceVisibleCells = filtersComplete ? records.data.cells : [];
  const visibleCells = sourceVisibleCells.map((cell) => {
    const override = previewOverrides[makeCellDomKey(cell)];
    return override ? { ...cell, ...override } : cell;
  });
  const rows = groupCellsByStudent({ ...records.data, cells: visibleCells });
  const statusCounts = createVisibleStatusCounts(visibleCells);

  const saveStatus = async (
    cell: AttendanceCellRecord,
    status: AttendanceStatus,
    note = cell.note,
  ) => {
    setPreviewOverrides((current) => ({
      ...current,
      [makeCellDomKey(cell)]: {
        status,
        note: note ?? null,
        version: cell.version + 1,
      },
    }));
    setMessage("Preview mode only. Use New Records to record attendance.");
  };

  const toggleNoteEditor = (cell: AttendanceCellRecord) => {
    const key = makeCellDomKey(cell);
    setOpenNoteKeys((current) => {
      if (current[key]) {
        const next = { ...current };
        delete next[key];
        return next;
      }
      return { ...current, [key]: true };
    });
  };

  const saveNote = (cell: AttendanceCellRecord, note: string) => {
    setPreviewOverrides((current) => ({
      ...current,
      [makeCellDomKey(cell)]: {
        status: cell.status,
        note: note || null,
        version: cell.version + 1,
      },
    }));
    setMessage("Preview mode only. Use New Records to record attendance.");
  };

  const exportTeacherViewCsv = async () => {
    const result = await bundle.transport.mutate("exportAttendanceContext", {
      input: {
        context: "teacher-view",
        format: "csv",
        recordsFilter: filter,
        includeNotes: true,
      },
    });
    deliverExportResult(result);
  };

  const exportTeacherViewPdf = async () => {
    const result = await bundle.transport.mutate("exportAttendanceContext", {
      input: {
        context: "teacher-view",
        format: "pdf",
        recordsFilter: filter,
        includeNotes: true,
      },
    });
    deliverExportResult(result);
  };

  return (
    <AttendanceShell
      bundle={bundle}
      title="Teacher View"
      subtitle="Admin preview of classroom attendance entry."
      testId="attendance-teacher-view-root"
      showPageHeader={false}
      actions={
        <ExportDropdown
          onExport={filtersComplete ? exportTeacherViewCsv : undefined}
          onExportPdf={filtersComplete ? exportTeacherViewPdf : undefined}
        />
      }
    >
      {showPreviewBanner ? (
        <div
          role="status"
          data-testid="attendance-teacher-preview-warning"
          className="mb-4 flex items-start gap-3 rounded-[12px] bg-[rgba(38,151,162,0.08)] py-2.5 pl-3 pr-2 text-sm text-[var(--text-secondary)]"
        >
          <Info
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--primary)]"
            strokeWidth={2}
          />
          <p className="flex-1 text-sm leading-snug text-[var(--text-secondary)]">
            <span className="font-semibold text-[var(--iron-grey)]">Preview:</span>{" "}
            This is what teachers will see when teacher access is rolled out in a future release.
            Status changes in this view are not saved — use the New Records tab to record attendance.
          </p>
          <button
            type="button"
            aria-label="Dismiss preview banner"
            onClick={() => setShowPreviewBanner(false)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[var(--text-secondary)] transition-colors hover:bg-white/70 hover:text-[var(--iron-grey)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/40"
          >
            <X aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      ) : null}
      <FilterControls
        config={context.data.config}
        state={state}
        setState={setState}
        showClassroomPaging={false}
        showWeekTabs={false}
        showMode={false}
        allowAllPeriods={false}
        compactLayout
        maxDate={teacherMaxDate}
      />
      {records.error || context.error ? (
        <ErrorState message={errorMessageFor(records.error ?? context.error, "Unable to load teacher preview.")} />
      ) : !filtersComplete ? (
        <EmptyState message="Select a classroom to begin previewing a teacher attendance roster." />
      ) : (
        <div className="space-y-4">
          <p
            role="status"
            className={message ? "text-sm text-[var(--text-secondary)]" : "sr-only"}
          >
            {message || "Teacher preview ready."}
          </p>
          <div
            role="table"
            aria-label="Teacher View attendance roster"
            className="overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white shadow-[0_0_3px_2px_rgba(206,242,237,0.39)]"
          >
            <div
              role="row"
              className="flex items-center justify-between border-b border-[#E5E7EB] bg-[#F4F6F3] px-4 py-3"
            >
              <span
                role="columnheader"
                className="text-[12px] font-semibold uppercase leading-[16.8px] text-[#6B7280]"
              >
                Student
              </span>
              <span
                role="columnheader"
                className="hidden text-[12px] font-semibold uppercase leading-[16.8px] text-[#6B7280] sm:block"
              >
                Status
              </span>
            </div>
            <div role="rowgroup">
              {rows.map((row, rowIndex) => {
                const cell = row.cells[0];
                if (!cell) return null;
                const studentName = getStudentName(context.data.config, row.studentId);
                const gradeLabel = getStudentGradeLabel(context.data.config, row.studentId);
                const cellKey = makeCellDomKey(cell);
                const noteOpen = Boolean(openNoteKeys[cellKey]);
                const hasNote = Boolean(cell.note);
                return (
                  <div
                    key={row.studentId}
                    role="row"
                    className="flex flex-col px-2 py-3 transition-colors hover:bg-[rgba(244,246,243,0.5)] sm:px-4 sm:py-4"
                    style={{
                      borderBottom: rowIndex === rows.length - 1 ? "0" : "1px solid #E5E7EB",
                    }}
                  >
                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
                      <div role="cell" className="flex min-w-[200px] items-center gap-3">
                        <div
                          aria-hidden="true"
                          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E5E7EB] bg-[#F4F6F3] text-[14px] font-normal uppercase leading-[21px] text-[#404F4D]"
                        >
                          {getStudentInitials(studentName)}
                        </div>
                        <div>
                          <div className="text-[14px] font-medium leading-[21px] text-[#404F4D]">
                            {formatStudentNameLastFirst(studentName)}
                          </div>
                          {gradeLabel ? (
                            <div className="text-[11px] font-medium leading-[14.3px] text-[#6B7280]">
                              {gradeLabel}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div
                        role="cell"
                        className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-4"
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex w-full items-center gap-2 overflow-x-auto rounded-[8px] border border-[#E5E7EB] bg-[#F4F6F3] p-1 sm:w-auto">
                            {statusOrder.map((status) => (
                              <button
                                key={status}
                                type="button"
                                aria-pressed={cell.status === status}
                                onClick={() => void saveStatus(cell, status)}
                                className={teacherStatusButtonClassName(status, cell.status === status)}
                                style={teacherStatusButtonStyle(status, cell.status === status)}
                              >
                                {STATUS_UI_LABELS[status].label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-label="Add note"
                          title="Add note"
                          data-testid="attendance-cell-notes-trigger"
                          onClick={() => toggleNoteEditor(cell)}
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] p-2 transition-colors ${
                            hasNote || noteOpen
                              ? "bg-[rgba(38,151,162,0.10)] text-[var(--primary)]"
                              : "bg-transparent text-[#9CA3AF] hover:bg-[#F4F6F3] hover:text-[#6B7280]"
                          }`}
                          style={
                            hasNote || noteOpen
                              ? { backgroundColor: "rgba(38,151,162,0.10)", color: "#2697A2" }
                              : { backgroundColor: "transparent", color: "#9CA3AF" }
                          }
                        >
                          <MessageSquare aria-hidden="true" className="h-5 w-5" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                    {noteOpen ? (
                      <div className="mt-3 sm:ml-[52px]">
                        <input
                          type="text"
                          aria-label={`Note for ${formatStudentNameLastFirst(studentName)}`}
                          value={cell.note ?? ""}
                          onChange={(event) => saveNote(cell, event.target.value)}
                          className="h-[39px] w-full rounded-[6px] border border-[#E5E7EB] bg-white px-3 py-2 text-[14px] leading-[21px] text-[#404F4D] placeholder:text-[#9CA3AF] focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[rgba(38,151,162,0.18)]"
                          placeholder="Add a note (e.g., late bus, doctor appt)..."
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="sticky bottom-4 z-10 mt-6 flex flex-col items-center justify-between gap-4 rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-[0_0_3px_2px_rgba(206,242,237,0.39)] sm:flex-row">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px] leading-[21px] text-[#404F4D]">
              <span>Present: <strong>{statusCounts.present}</strong></span>
              <span>Absent: <strong>{statusCounts.absent}</strong></span>
              <span>Late: <strong>{statusCounts.late}</strong></span>
              <span>Excused: <strong>{statusCounts.excused}</strong></span>
            </div>
            <button
              type="button"
              disabled
              title="Preview mode — use New Records to record attendance"
              aria-disabled="true"
              className="inline-flex h-11 w-full cursor-not-allowed items-center justify-center rounded-[6px] bg-[var(--primary)] px-6 py-2.5 text-[16px] font-medium leading-[24px] text-white opacity-50 shadow-[0_0_3px_2px_rgba(206,242,237,0.39)] sm:w-auto"
            >
              Submit Attendance
            </button>
          </div>
        </div>
      )}
    </AttendanceShell>
  );
};

const createVisibleStatusCounts = (cells: readonly AttendanceCellRecord[]) => {
  const counts = {
    present: 0,
    absent: 0,
    late: 0,
    excused: 0,
  } as Record<AttendanceStatus, number>;
  cells.forEach((cell) => {
    counts[cell.status] += 1;
  });
  return counts;
};

const StudentHistoryPage = ({ bundle }: { bundle: AttendanceFeatureBundle }) => {
  const { studentId = "" } = useParams();
  const context = useAttendanceContext(bundle);
  const historyDates = getRollingSchoolDatesEndingAt(
    context.data.currentSchoolDate || DEFAULT_CONTEXT.currentSchoolDate,
    context.data.config.schoolCalendar.activeWeekdays,
  );
  const fromSchoolDate = historyDates[0] ?? context.data.currentSchoolDate ?? DEFAULT_CONTEXT.currentSchoolDate;
  const toSchoolDate = historyDates[historyDates.length - 1] ?? context.data.currentSchoolDate ?? DEFAULT_CONTEXT.currentSchoolDate;
  const history = useAsyncQuery<AttendanceStudentHistory>(
    () => bundle.transport.query("getAttendanceStudentHistory", {
      input: {
        ...bundle.runtimeScope,
        studentId,
        fromSchoolDate,
        toSchoolDate,
      },
    }),
    [bundle.transport, bundle.runtimeScope.organizationId, bundle.runtimeScope.schoolId, studentId, fromSchoolDate, toSchoolDate],
    {
      studentId,
      studentName: getStudentName(context.data.config, studentId),
      route: `/school/attendance/students/${encodeURIComponent(studentId)}`,
      range: { fromSchoolDate, toSchoolDate },
      summaryTotals: {
        present: 0,
        absent: 0,
        late: 0,
        excused: 0,
        absenceMetricCount: 0,
        attendanceRatePercent: 100,
      },
      rows: [],
    },
  );
  const studentMembership = context.data.config.rosterMemberships.find(
    (membership) => membership.studentId === studentId,
  );
  const studentsFamiliesRouteBase = bundle.routeManifest.studentsFamiliesRouteBase.replace(/\/+$/, "");
  const studentsFamiliesLink = studentMembership?.familyId
    ? `${studentsFamiliesRouteBase}/family/${encodeURIComponent(studentMembership.familyId)}/children?studentId=${encodeURIComponent(studentId)}`
    : `${studentsFamiliesRouteBase}/dashboard?studentId=${encodeURIComponent(studentId)}`;
  const summaryTotals = history.data.summaryTotals;

  return (
    <AttendanceShell
      bundle={bundle}
      title="Student History"
      subtitle={`${history.data.studentName} / Read-only attendance summary`}
      testId="attendance-student-history-root"
    >
      {history.error || context.error ? (
        <ErrorState message={errorMessageFor(history.error ?? context.error, "Unable to load student attendance history.")} />
      ) : history.isLoading || context.isLoading ? (
        <EmptyState message="Loading student attendance history." />
      ) : (
        <>
      <section className="mb-5 flex flex-col gap-3 rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)] p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold text-[var(--iron-grey)]">
            Attendance history for {history.data.studentName}
          </p>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Read-only 30-school-day detail from Attendance. Student profile edits live in Students & Families.
          </p>
        </div>
        <Link
          to={studentsFamiliesLink}
          className="inline-flex h-10 items-center rounded-[8px] border border-[var(--border)] bg-white px-4 text-sm font-semibold text-[var(--iron-grey)]"
        >
          Open in Students & Families
        </Link>
      </section>
      <section className="mb-5 grid gap-3 md:grid-cols-5">
        {[
          ["Present", summaryTotals.present],
          ["Absent", summaryTotals.absent],
          ["Late", summaryTotals.late],
          ["Excused", summaryTotals.excused],
          ["Attendance Rate", `${summaryTotals.attendanceRatePercent}%`],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[8px] border border-[var(--border)] bg-white p-3">
            <p className="text-sm font-medium text-[var(--text-secondary)]">{label}</p>
            <p className="mt-1 text-xl font-semibold text-[var(--iron-grey)]">{value}</p>
          </div>
        ))}
      </section>
      <div className="overflow-x-auto rounded-[8px] border border-[var(--border)] bg-[var(--surface-light)]">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-white">
              {["Date", "Period", "Class", "Status", "Notes", "Recorded By"].map((column) => (
                <th key={column} className="px-4 py-3 font-semibold text-[var(--iron-grey)]">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.data.rows.map((row) => (
              <tr key={`${row.schoolDate}:${row.classroomId}:${row.periodId}:${row.version}`} className="border-b border-[var(--border)] last:border-b-0">
                <td className="px-4 py-3">{formatDate(row.schoolDate)}</td>
                <td className="px-4 py-3">{row.periodLabel}</td>
                <td className="px-4 py-3">{row.classroomLabel}</td>
                <td className="px-4 py-3">{STATUS_UI_LABELS[row.status].label}</td>
                <td className="px-4 py-3">{row.note ?? ""}</td>
                <td className="px-4 py-3">Read-only</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}
    </AttendanceShell>
  );
};

export const createAttendanceRoutes = (bundle: AttendanceFeatureBundle) => (
  <>
    <Route index element={<OverviewPage bundle={bundle} />} />
    <Route path="dashboard" element={<OverviewPage bundle={bundle} />} />
    <Route path="control-plane" element={<AttendanceControlPlanePage bundle={bundle} />} />
    <Route path="new-records" element={<RecordsPage bundle={bundle} />} />
    <Route path="records" element={<Navigate to="../new-records" replace />} />
    <Route path="roster" element={<RosterPage bundle={bundle} />} />
    <Route path="reports" element={<ReportsPage bundle={bundle} />} />
    <Route path="teacher-view" element={<TeacherViewPage bundle={bundle} />} />
    <Route path="students/:studentId" element={<StudentHistoryPage bundle={bundle} />} />
    <Route path="*" element={<Navigate to="." replace />} />
  </>
);

export const AttendanceRouteFamily = ({
  bundle,
}: {
  bundle: AttendanceFeatureBundle;
}) => <Routes>{createAttendanceRoutes(bundle)}</Routes>;
